import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  X, CaretRight, CaretLeft, DeviceMobile, DeviceTablet, Laptop, Watch,
  Wrench, CheckCircle, User, Plus, MagnifyingGlass, Cpu, Printer, Check,
  Warning, DotsThreeVertical, PencilSimple, ShoppingBag, Trash, Phone, EnvelopeSimple, ArrowSquareOut,
  Globe, CaretDown, Receipt, CreditCard, MapPin, NotePencil, Sparkle, GearSix, PaperPlaneTilt, Money, Wallet,
} from "@phosphor-icons/react";
import {
  MANUFACTURERS, DEVICE_TYPES, getModels, PROBLEMS, orderRepairs,
  arrangeProblems, repairOrderScope, resolveRepairLayout, REPAIR_GROUPS,
} from "../data/repairCatalog";

// Brand mark for Stripe — same inline "S" glyph used on the invoice
// modal's "Send Invoice" button, kept consistent across the app.
const StripeMark = ({ className = "h-4 w-auto shrink-0" }) => (
  <svg className={className} viewBox="0 0 28 28" fill="none">
    <rect width="28" height="28" rx="6" fill="currentColor" fillOpacity="0.16" />
    <path d="M13.3 11.2c0-.65.54-.9 1.43-.9 1.28 0 2.9.39 4.17 1.08V7.72c-1.4-.55-2.78-.77-4.17-.77-3.41 0-5.68 1.78-5.68 4.76 0 4.64 6.38 3.9 6.38 5.9 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.34v3.7a11.6 11.6 0 0 0 4.58.95c3.49 0 5.89-1.73 5.89-4.75-.01-5.01-6.42-4.12-6.42-6Z" fill="currentColor" />
  </svg>
);
import SaleCompleteCard from "./SaleCompleteCard";
import PostTicketActionsModal from "./PostTicketActionsModal";
import TyroPaymentDialog from "./TyroPaymentDialog";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { cacheWizardCatalogPart, getCachedWizardCatalog, invalidateWizardCatalogCache, prefetchWizardCatalog } from "../lib/catalogCache";

const DEVICE_ICON = {
  // Legacy preset ids (still used if any local catalog entries survive)
  phone: DeviceMobile, tablet: DeviceTablet, laptop: Laptop, watch: Watch, other: Cpu,
  // Website catalog kebab-case slugs — cover the top device families
  // seen across Apple / Samsung / Google / Xiaomi / etc.
  iphone: DeviceMobile, ipad: DeviceTablet, macbook: Laptop, "apple-watch": Watch,
  "galaxy-phone": DeviceMobile, "galaxy-tab": DeviceTablet, "galaxy-book": Laptop, "galaxy-watch": Watch,
  "pixel-phone": DeviceMobile, "pixel-tab": DeviceTablet, "pixel-book": Laptop, "pixel-watch": Watch,
  smartphone: DeviceMobile, mobile: DeviceMobile,
  desktop: Cpu, imac: Cpu, "mac-mini": Cpu, "mac-pro": Cpu,
};

/** Return the best-fit device icon for a website-catalog slug or name.
 *  Falls back to a name-based heuristic before defaulting to `Cpu`. */
function resolveDeviceIcon(id, label) {
  if (id && DEVICE_ICON[id]) return DEVICE_ICON[id];
  const s = `${id || ""} ${label || ""}`.toLowerCase();
  if (s.match(/phone|iphone|pixel|galaxy s|smartphone|mobile/)) return DeviceMobile;
  if (s.match(/pad|tab|tablet/)) return DeviceTablet;
  if (s.match(/book|laptop|notebook/)) return Laptop;
  if (s.match(/watch/)) return Watch;
  return Cpu;
}

/** Public CDN URL for a brand's SVG logo. Uses simpleicons.org — free,
 *  no API key, 3000+ brands, kebab-case slugs match our website ids.
 *  A few brands need a slug remap because their simpleicons name differs
 *  from what our catalog exposes (e.g. Microsoft's slug is "windows"
 *  for the current Windows logo, or "xbox" for gaming — neither matches
 *  a corporate mark; we use their newer stylised "microsoftazure"). */
const BRAND_SLUG_ALIAS = {
  microsoft: "microsoft",
  ms: "microsoft",
  hp: "hp",
  "hewlett-packard": "hp",
};
function brandLogoUrl(id) {
  const raw = (id || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const slug = BRAND_SLUG_ALIAS[raw] || raw;
  if (!slug) return "";
  return `https://cdn.simpleicons.org/${slug}`;
}

/** Generate a monogram token (1-2 caps letters) from a brand name. Used
 *  as the fallback when the logo CDN misses (unknown/new brand). */
function brandMonogram(name) {
  if (!name) return "?";
  const cleaned = name.replace(/[^A-Za-z0-9\s]/g, "").trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return cleaned.slice(0, 2).toUpperCase();
}

function inferLocalDeviceTypeBrand(label) {
  const text = ` ${(label || "").toLowerCase()} `;
  const hints = [
    ["samsung", ["samsung", "galaxy", "note ", " z fold", " z flip", "fold", "flip"]],
    ["apple", ["apple", "iphone", "ipad", "macbook", "imac", "apple watch"]],
    ["google", ["google", "pixel"]],
    ["oneplus", ["oneplus", "one plus"]],
    ["xiaomi", ["xiaomi", "redmi", "poco"]],
    ["huawei", ["huawei"]],
    ["motorola", ["motorola", "moto", "razr"]],
    ["sony", ["sony", "xperia"]],
    ["lg", ["lg ", " lg", "thinq", "velvet"]],
    ["nokia", ["nokia"]],
    ["oppo", ["oppo"]],
    ["asus", ["asus", "rog", "zenbook", "vivobook"]],
  ];
  for (const [mfr, needles] of hints) {
    if (needles.some((n) => text.includes(n))) return mfr;
  }
  return "";
}

// Colour palette for brand monograms — cycled by name hash so each brand
// gets a stable colour that stays consistent across sessions.
const BRAND_COLOURS = [
  { bg: "bg-blue-100",    text: "text-blue-700" },
  { bg: "bg-emerald-100", text: "text-emerald-700" },
  { bg: "bg-purple-100",  text: "text-purple-700" },
  { bg: "bg-amber-100",   text: "text-amber-700" },
  { bg: "bg-rose-100",    text: "text-rose-700" },
  { bg: "bg-indigo-100",  text: "text-indigo-700" },
  { bg: "bg-teal-100",    text: "text-teal-700" },
  { bg: "bg-orange-100",  text: "text-orange-700" },
  { bg: "bg-slate-200",   text: "text-slate-800" },
];
function brandColour(id) {
  const s = id || "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i) | 0;
  return BRAND_COLOURS[Math.abs(h) % BRAND_COLOURS.length];
}

const STEPS = [
  { key: "manufacturer", label: "Brand", hint: "Pick a manufacturer" },
  { key: "device", label: "Device", hint: "Type of device" },
  { key: "model", label: "Model", hint: "Exact model" },
  { key: "problem", label: "Issues", hint: "What's wrong?" },
  { key: "review", label: "Customer", hint: "Attach customer" },
  { key: "checkout", label: "Finish", hint: "Create & print" },
];

export default function NewTicketWizard({ open, onClose, onCreated }) {
  const nav = useNavigate();
  const { currentStore } = useStore();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [step, setStep] = useState(0);
  // One key per checkout attempt — reused across Tyro retries/re-entries
  // so a network retry or double "Complete Sale" click can never ring up
  // two tickets/sales (mirrors the Idempotency-Key guard on /sales).
  const checkoutIdemKeyRef = useRef(null);
  const getCheckoutIdemKey = () => {
    if (!checkoutIdemKeyRef.current) {
      checkoutIdemKeyRef.current = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    }
    return checkoutIdemKeyRef.current;
  };

  // Post-sale actions popup shown after checkout
  const [postSale, setPostSale] = useState(null); // { sale, customer, ticket } | null
  // Approved-but-unsaved Tyro charge: keep the meta so Retry save re-sends the
  // SAME request (same Idempotency-Key) without charging the card again.
  const [tyroSaveError, setTyroSaveError] = useState(null);
  const lastTyroMetaRef = useRef(null);
  // Post-ticket actions popup shown after "Ticket only" / "Create tickets"
  // (no checkout). Mirrors postSale but for the ticket-only intake flows.
  const [postTicket, setPostTicket] = useState(null); // { ticket, tickets, customer } | null

  const [manufacturer, setManufacturer] = useState(null);
  const [manufacturerOther, setManufacturerOther] = useState("");
  const [deviceType, setDeviceType] = useState(null);
  const [model, setModel] = useState("");
  // The website catalog's stable slug for the picked model (e.g.
  // "macbook-pro-13-2020-a2251"). Multiple models share the same
  // display name (three "MacBook Pro 13"" tiles for different years),
  // so we CANNOT use name for selection matching — otherwise clicking
  // one tile would highlight all three. Kept in sync with `model` (the
  // display name) and cleared alongside it.
  const [modelId, setModelId] = useState("");
  // Meta about the currently-selected model (from the website catalog).
  // Kept alongside `model` so the plain name still drives the highlight
  // match on the picker; on submit the meta appends "· 2020 · A2337" to
  // the ticket's `device_model` for a receipt-friendly display.
  const [modelMeta, setModelMeta] = useState({ year: "", model_number: "" });
  // Separate input from the selected model so typing filters the grid without
  // overwriting the committed selection — clicking a tile fills both.
  const [modelQuery, setModelQuery] = useState("");
  const [problems, setProblems] = useState([]);
  const [problemPrices, setProblemPrices] = useState({});
  const [editingProblemPriceId, setEditingProblemPriceId] = useState("");
  const [problemNotes, setProblemNotes] = useState("");
  const [customProblems, setCustomProblems] = useState([]); // [{id, label, price}] — session-local
  // Per-problem selected part option (only for website repairs with
  // multiple part_options like OEM / Soft OLED / Incell). Shape:
  //   { "web_screen-repair": "oem" }
  // Absent key → defaults to the FIRST option in the repair's list.
  const [problemPartOptionIds, setProblemPartOptionIds] = useState({});
  const [customFormOpen, setCustomFormOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({ label: "", price: "", scope: "model" }); // scope: "model" | "device" | "all"

  // Part stock availability for wizard (stock shown by default so cashiers
  // always see what's on the shelf; toggle can still hide it).
  const [showStockAvailability, setShowStockAvailability] = useState(true);
  const [wizardStockMap, setWizardStockMap] = useState({});
  useEffect(() => {
    api.get("/repair-parts/wizard-stock").then((r) => setWizardStockMap(r.data || {})).catch(() => {});
  }, []);

  const cachedStartupCatalog = useMemo(() => getCachedWizardCatalog(), []);

  // Loaded-from-backend catalog extensions (persistent per store)
  const [catalog, setCatalog] = useState(cachedStartupCatalog.repairCatalog || { manufacturer: [], device_type: [], model: [], problem: [] });
  const loadCatalog = async ({ force = false } = {}) => {
    const cached = getCachedWizardCatalog().repairCatalog;
    if (cached && !force) setCatalog(cached);
    try {
      const { data } = await api.get("/repair-catalog");
      const next = data || { manufacturer: [], device_type: [], model: [], problem: [] };
      cacheWizardCatalogPart("repair", next);
      setCatalog(next);
      return next;
    } catch {
      return cached || null;
    }
  };
  useEffect(() => {
    if (!open) return;
    const cached = getCachedWizardCatalog();
    if (cached.repairCatalog) setCatalog(cached.repairCatalog);
    prefetchWizardCatalog().then((fresh) => {
      if (fresh.repairCatalog) setCatalog(fresh.repairCatalog);
      if (fresh.websiteCatalog) setWebsiteCatalog(fresh.websiteCatalog);
      if (fresh.websiteOrders) setWebsiteOrders(fresh.websiteOrders);
      if (fresh.manufacturerOrder) setMfrOrder(fresh.manufacturerOrder);
      if (fresh.deviceOrder) setDevOrder(fresh.deviceOrder);
    }).catch(() => {});
  }, [open]);

  // Website-synced repair catalog (manufacturers → device_types → models →
  // repairs → part_options). Pulled nightly from the merchant's website by
  // the sync scheduler; here we just read the merged tree and use fuzzy
  // matching on the currently-selected manufacturer/model to surface
  // live-priced repair options above the manual problem grid.
  const [websiteCatalog, setWebsiteCatalog] = useState(cachedStartupCatalog.websiteCatalog || { manufacturers: [] });
  // Chain-wide catalog ordering (manufacturers, device_types, models,
  // repairs). Loaded once when the wizard opens. Keys are scope strings:
  //   "manufacturers", "device_types:<mfr_id>", "models:<mfr_id>:<dev_id>",
  //   "repairs:<mfr_id>:<dev_id>:<mdl_id>". Values are ordered arrays of ids.
  const [websiteOrders, setWebsiteOrders] = useState(cachedStartupCatalog.websiteOrders || {});

  // Inline-add state for each step
  const [addMfrOpen, setAddMfrOpen] = useState(false);
  const [addMfrLabel, setAddMfrLabel] = useState("");
  const [addDevOpen, setAddDevOpen] = useState(false);
  const [addDevLabel, setAddDevLabel] = useState("");
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [addModelLabel, setAddModelLabel] = useState("");
  const [catalogEditor, setCatalogEditor] = useState(null);
  const [catalogSaving, setCatalogSaving] = useState(false);

  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "" });
  const [phoneMatches, setPhoneMatches] = useState([]); // existing customers with matching phone
  const [checkingPhone, setCheckingPhone] = useState(false);
  // Inline edit state — lets staff fix a typo in a previously-saved customer
  // right from the picker row without leaving the wizard.
  const [editCustId, setEditCustId] = useState("");
  const [editCust, setEditCust] = useState({ name: "", phone: "", email: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [imei, setImei] = useState("");
  const [passcode, setPasscode] = useState("");
  // Warranty is no longer selected in the wizard — the printed warranty terms
  // come from the store's Templates settings (service_warranty_terms /
  // inventory_warranty_terms). We keep `warrantyMonths=0` so the ticket
  // doesn't carry a hard "covered until" date that would shadow the
  // template-driven copy.
  const [warrantyMonths, setWarrantyMonths] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  // Split-tender state — when on, the wizard checkout uses per-method
  // amounts (Cash / Card / Other) instead of the single tendered field.
  const [splitMode, setSplitMode] = useState(false);
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [splitOther, setSplitOther] = useState("");
  // Stripe split-tender slice — unlike cash/card/tyro/other this isn't
  // collected now; it's excluded from amount_tendered and automatically
  // sent as a Stripe payment link right after the ticket+sale is created.
  const [splitStripe, setSplitStripe] = useState("");
  // Tyro EFTPOS split-tender amount — separate from card_manual so the
  // wizard can route the slice through the real terminal SDK while keeping
  // cash / card / other unchanged.
  const [splitTyro, setSplitTyro] = useState("");
  // Tyro dialog state. `tyroOpen` is the single-tender purchase; the dialog
  // for a split-tender Tyro slice reuses the same component but is gated
  // by `tyroSplitOpen` so only the Tyro amount is charged (not the whole
  // total). On approval, submitCheckout(tyroMeta) is re-entered with the
  // SDK result so the resulting Sale row picks up auth code + RRN.
  const [tyroOpen, setTyroOpen] = useState(false);
  const [tyroSplitOpen, setTyroSplitOpen] = useState(false);
  const [collectPayment, setCollectPayment] = useState(false);
  // Accessories/products added to the same invoice at intake time — e.g. the
  // customer buys a case, screen protector, or charger while dropping off.
  // Each entry: { product_id, sku, name, unit_price, quantity, stock }
  const [accessories, setAccessories] = useState([]);
  const [accessorySearch, setAccessorySearch] = useState("");
  const [accessoryResults, setAccessoryResults] = useState([]);
  const [accessorySearchOpen, setAccessorySearchOpen] = useState(false);
  // Which accessory row is currently in "edit price" mode (product_id or "")
  const [editingPriceId, setEditingPriceId] = useState("");
  const [editingPriceValue, setEditingPriceValue] = useState("");

  // Print prefs (for auto-print after create)
  const [prefs, setPrefs] = useState({
    label_printer_id: null, auto_print_label: false,
    receipt_printer_id: null, auto_print_receipt: false,
  });
  const [printConfigured, setPrintConfigured] = useState(false);
  const [printOnCreate, setPrintOnCreate] = useState(true);

  // ——— Multi-device intake (Option A) ———
  // `batch` holds devices the user has already filled out and parked via
  // "+ Add another device". Each entry is a full ticket draft; the currently
  // visible wizard state represents the device being edited right now.
  //
  // Shape (per entry):
  //   { uid, device_make, device_model, imei, passcode, issue,
  //     problems:[problemId], problem_prices:{id:number|""},
  //     problem_notes, warranty_months, estimated_cost,
  //     _ui: { manufacturer, deviceType, customProblems } }
  const [batch, setBatch] = useState([]);
  const batchSubtotal = batch.reduce((s, d) => s + Number(d.estimated_cost || 0), 0);

  useEffect(() => {
    if (open) {
      setStep(0);
      setManufacturer(null); setManufacturerOther("");
      setDeviceType(null); setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
      setProblems([]); setProblemPrices({}); setProblemNotes("");
      setCustomProblems([]); setCustomFormOpen(false); setCustomDraft({ label: "", price: "", scope: "model" });
      setCustomerId(""); setCustomerSearch("");
      setShowNewCust(false); setNewCust({ name: "", phone: "", email: "" });
      setImei(""); setPasscode(""); setWarrantyMonths(0);
      setPaymentMethod("cash"); setTendered(""); setCollectPayment(false);
      setSplitMode(false); setSplitCash(""); setSplitCard(""); setSplitTyro(""); setSplitOther("");
      setTyroOpen(false); setTyroSplitOpen(false);
      setAccessories([]); setAccessorySearch(""); setAccessoryResults([]); setAccessorySearchOpen(false);
      setBatch([]);
      // Load print prefs + status quietly (scoped to current store via interceptor)
      api.get("/print/status").then((r) => setPrintConfigured(!!r.data?.configured)).catch(() => {});
      api.get("/users/me/print-prefs")
        .then((r) => {
          setPrefs(r.data || {});
          // default on if user has a label printer configured
          setPrintOnCreate(!!r.data?.label_printer_id);
        })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => { if (step === 4) setCustomers([]); }, [step]);
  // Step 5 (Finish) safety guard — when the user lands on this step
  // via CONTINUE the focus often inherits onto the "Pay & Create"
  // button and a held-Enter or double-tap can fire creation before
  // the cashier has even seen the screen. We disable the create
  // action for 700ms after entering step 5 to neutralise both
  // accidental keyboard-chain and double-tap on touchscreens.
  const [finishArmed, setFinishArmed] = useState(false);
  useEffect(() => {
    if (step !== 5) { setFinishArmed(false); return; }
    setFinishArmed(false);
    const t = setTimeout(() => setFinishArmed(true), 700);
    // Steal focus off whatever was focused at step-4 → step-5 transition
    // so Enter / Space can't chain through to the Create button.
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    return () => clearTimeout(t);
  }, [step]);
  useEffect(() => {
    if (step !== 4) return;
    const q = customerSearch.trim();
    // Don't flood the picker with every customer — only search once the
    // user has typed at least 2 characters. Prevents the 20k-contact
    // address book from dumping onto the screen the instant the step opens.
    if (q.length < 2) { setCustomers([]); return; }
    const t = setTimeout(() => loadCustomers(q), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [customerSearch, step]);

  // Debounced phone duplicate check when creating a new customer
  const normalizePhone = (p) => (p || "").replace(/[^0-9+]/g, "");
  useEffect(() => {
    if (!showNewCust) { setPhoneMatches([]); return; }
    const normalized = normalizePhone(newCust.phone);
    if (normalized.length < 4) { setPhoneMatches([]); return; }
    setCheckingPhone(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q: newCust.phone.trim() } });
        const exacts = data.filter((c) => {
          const cp = normalizePhone(c.phone);
          return cp && (cp === normalized || cp.endsWith(normalized) || normalized.endsWith(cp));
        });
        setPhoneMatches(exacts.slice(0, 3));
      } catch {
        setPhoneMatches([]);
      } finally {
        setCheckingPhone(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [newCust.phone, showNewCust]);

  // Customer.id -> [open booking docs]. Populated alongside the customer
  // search results so the picker can flag customers with unconverted
  // bookings (status in [new, confirmed]).
  const [openBookingsByCustomer, setOpenBookingsByCustomer] = useState({});

  const loadCustomers = (q) =>
    api.get("/customers", { params: q ? { q, limit: 12 } : { limit: 12 } })
      .then(async (r) => {
        const hits = (r.data || []).slice(0, 12);
        setCustomers(hits);
        // Fetch open bookings per result row in parallel. Soft-fail per
        // row — a 4xx on one result doesn't poison the dropdown.
        const ids = hits.map((c) => c.id).filter(Boolean);
        if (ids.length === 0) { setOpenBookingsByCustomer({}); return; }
        const results = await Promise.allSettled(
          ids.map((cid) =>
            api.get("/bookings", { params: { customer_id: cid, open_only: true } })
          )
        );
        const map = {};
        ids.forEach((cid, idx) => {
          const r2 = results[idx];
          if (r2.status === "fulfilled") map[cid] = r2.value.data || [];
        });
        setOpenBookingsByCustomer(map);
      });

  // Full-delete from the picker. Gated (confirm) and falls back gracefully
  // on permission error — non-admins see a friendly message.
  const deleteCustomerFromPicker = async (c) => {
    if (!c?.id) return;
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
      if (customerId === c.id) setCustomerId("");
      toast.success("Customer deleted");
      cancelEditCustomer();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Open inline editor for a customer row in the picker.
  const startEditCustomer = (c) => {
    setEditCustId(c.id);
    setEditCust({ name: c.name || "", phone: c.phone || "", email: c.email || "" });
  };
  const cancelEditCustomer = () => {
    setEditCustId("");
    setEditCust({ name: "", phone: "", email: "" });
  };
  const saveEditCustomer = async () => {
    if (!editCustId) return;
    const patch = {
      name: (editCust.name || "").trim(),
      phone: (editCust.phone || "").trim(),
      email: (editCust.email || "").trim(),
    };
    if (!patch.name) { toast.error("Name can't be empty"); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/customers/${editCustId}`, patch);
      // Patch the row in the current list without a full reload.
      setCustomers((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      toast.success("Customer updated");
      cancelEditCustomer();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  // Custom display order for models, keyed by (manufacturer, device_type).
  // Loaded from /api/repair-catalog/model-order and merged with the preset list.
  const [modelOrder, setModelOrder] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  // Generic wiggle-mode reorder for Manufacturer / Device-type / Problem.
  // Each list keeps its own state so they can be re-ordered independently.
  const [mfrOrder, setMfrOrder] = useState(cachedStartupCatalog.manufacturerOrder || []);
  const [mfrReorder, setMfrReorder] = useState(false);
  const [mfrDragIdx, setMfrDragIdx] = useState(null);
  const [devOrder, setDevOrder] = useState(cachedStartupCatalog.deviceOrder || []);
  const [devReorder, setDevReorder] = useState(false);
  const [devDragIdx, setDevDragIdx] = useState(null);
  const [probOrder, setProbOrder] = useState([]);
  const [probReorder, setProbReorder] = useState(false);
  const [probDragIdx, setProbDragIdx] = useState(null);

  useEffect(() => {
    if (!open || !manufacturer?.id || !deviceType?.id || probReorder) return;
    api.get("/repair-catalog/list-order", {
      params: { kind: "problem", scope_key: `${manufacturer.id}|${deviceType.id}` },
    }).then((r) => setProbOrder(r.data.ordered_labels || []))
      .catch(() => setProbOrder([]));
  }, [open, manufacturer?.id, deviceType?.id, probReorder]);

  // Split catalog entries into "customs" (standalone) vs "overrides" (rename a preset).
  const mfrOverrides = useMemo(
    () => new Map((catalog.manufacturer || []).filter((c) => c.override_of).map((c) => [c.override_of, c.label])),
    [catalog.manufacturer]
  );
  const devOverrides = useMemo(
    () => new Map((catalog.device_type || []).filter((c) => c.override_of).map((c) => [c.override_of, c.label])),
    [catalog.device_type]
  );
  const modelOverrides = useMemo(
    () => new Map((catalog.model || []).filter((c) => c.override_of).map((c) => [c.override_of, c.label])),
    [catalog.model]
  );

  // Apply a saved order to any list by matching on ``label``. Falls back to
  // ``_originalLabel`` for preset items that have been renamed (so a saved
  // position "Apple" still wins when the current label is "Apple Inc").
  // Unknown labels fall to the tail in their source-order so new catalog
  // additions still appear in the list.
  const applyOrder = (items, order) => {
    if (!order || order.length === 0) return items;
    const idx = new Map(order.map((lbl, i) => [lbl, i]));
    const keyOf = (it) =>
      idx.has(it.label) ? idx.get(it.label)
      : it._originalLabel && idx.has(it._originalLabel) ? idx.get(it._originalLabel)
      : null;
    const known = [], rest = [];
    for (const it of items) (keyOf(it) !== null ? known : rest).push(it);
    known.sort((a, b) => keyOf(a) - keyOf(b));
    return [...known, ...rest];
  };

  /** applyIdOrder — like applyOrder but keys off a stable id field (not
   *  the display label). Used for website-catalog lists where the same
   *  display name can repeat across records (three "MacBook Pro 13\""s
   *  with different years) but each has a unique id. Falls back to alpha
   *  by label for anything not in the saved order. */
  const applyIdOrder = (items, order, idField = "id") => {
    const byId = new Map((order || []).map((id, i) => [id, i]));
    const known = [], rest = [];
    for (const it of items) {
      (byId.has(it[idField]) ? known : rest).push(it);
    }
    known.sort((a, b) => byId.get(a[idField]) - byId.get(b[idField]));
    rest.sort((a, b) => (a.label || a.name || "").localeCompare(b.label || b.name || ""));
    return [...known, ...rest];
  };

  // ────────────────────────────────────────────────────────────────────
  // WIZARD DATA SOURCES — website-catalog driven
  // ────────────────────────────────────────────────────────────────────
  // All four cascading lists (manufacturer → device type → model → issue)
  // read from the synced website catalog. Local `repair_catalog` entries
  // are intentionally NOT merged — they'd double-list the same brand
  // ("Apple" twice) once the sync landed. Local catalog is still visible
  // and editable via Settings → Repair Catalog for admin purposes; it
  // just doesn't clutter the intake flow anymore.
  //
  // Free-type escape hatches remain: an "Other" tile at the end of each
  // step lets staff type a value the website doesn't yet cover — the
  // typed value is captured on the ticket as a snapshot (no persistence).

  const allManufacturers = useMemo(() => {
    const wm = (websiteCatalog?.manufacturers || []).map((m) => ({ id: m.id, label: m.name }));
    // Locally-added manufacturers (from the wizard's "Add" flow) that
    // aren't already in the website catalog. Merged in so admins can
    // extend the list — the wizard was ignoring these before this fix.
    const websiteNames = new Set(wm.map((x) => x.label.toLowerCase()));
    const local = (catalog.manufacturer || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .map((c) => ({ id: `cat_${c.id}`, label: c.label, _custom: true }));
    const savedOrder = websiteOrders["manufacturers"] || [];
    return [...applyIdOrder([...wm, ...local], savedOrder), { id: "other", label: "Other" }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteCatalog, websiteOrders, catalog.manufacturer]);

  // For the currently-selected manufacturer, get its device types from the
  // website payload. If unmatched (staff typed a custom brand), we still
  // show "Other" so they can proceed.
  const selectedWebsiteMfr = useMemo(() => {
    if (!manufacturer?.id) return null;
    return (websiteCatalog?.manufacturers || []).find((m) => m.id === manufacturer.id) || null;
  }, [websiteCatalog, manufacturer]);

  const allDeviceTypes = useMemo(() => {
    const dts = (selectedWebsiteMfr?.device_types || []).map((d) => ({ id: d.id, label: d.name }));
    // Merge local device types the user has added under this manufacturer.
    const websiteNames = new Set(dts.map((x) => x.label.toLowerCase()));
    const currentMfrId = manufacturer?.id || "";
    const local = (catalog.device_type || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .filter((c) => {
        const parent = c.parent_manufacturer || inferLocalDeviceTypeBrand(c.label);
        if (!parent && c.parent_device_type !== "all") return false;
        return !parent || parent === currentMfrId;
      })
      .map((c) => ({
        id: `cat_${c.id}`,
        label: c.label,
        _custom: true,
        entryId: c.id,
        parent_manufacturer: c.parent_manufacturer || inferLocalDeviceTypeBrand(c.label),
        hidden: !!c.hidden,
      }));
    const scope = currentMfrId ? `device_types:${currentMfrId}` : "";
    const savedOrder = websiteOrders[scope] || [];
    return [...applyIdOrder([...dts, ...local], savedOrder), { id: "other", label: "Other" }];
  }, [selectedWebsiteMfr, websiteOrders, manufacturer, catalog.device_type]);

  const selectedWebsiteDeviceType = useMemo(() => {
    if (!selectedWebsiteMfr || !deviceType?.id) return null;
    return (selectedWebsiteMfr.device_types || []).find((d) => d.id === deviceType.id) || null;
  }, [selectedWebsiteMfr, deviceType]);

  const modelsList = useMemo(() => {
    const website = (selectedWebsiteDeviceType?.models || []).map((m) => ({
      id: m.id,
      name: m.name,
      year: m.year || "",
      model_number: m.model_number || "",
    }));
    // Merge local user-added models for the selected mfr + device type
    // so ADD MODEL entries surface in the wizard immediately.
    const websiteNames = new Set(website.map((m) => m.name.toLowerCase()));
    const currentMfrId = manufacturer?.id || "";
    const currentDevId = deviceType?.id || "";
    const local = (catalog.model || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .filter((c) => {
        const mfr = c.parent_manufacturer || "";
        const dev = c.parent_device_type || "";
        if (!mfr && (!dev || dev === "all")) return true;
        if (!mfr && dev) return dev === currentDevId;
        if (mfr && mfr !== currentMfrId) return false;
        if (dev && dev !== "all" && dev !== currentDevId) return false;
        return true;
      })
      .map((c) => ({
        id: `cat_${c.id}`,
        name: c.label,
        year: "",
        model_number: "",
        _custom: true,
        entryId: c.id,
        parent_manufacturer: c.parent_manufacturer || "",
        parent_device_type: c.parent_device_type || "",
        hidden: !!c.hidden,
      }));
    const merged = [...website, ...local];
    const scope = (currentMfrId && currentDevId) ? `models:${currentMfrId}:${currentDevId}` : "";
    const savedOrder = websiteOrders[scope] || [];
    return applyIdOrder(merged, savedOrder);
  }, [selectedWebsiteDeviceType, websiteOrders, manufacturer, deviceType, catalog.model]);

  // Reset modelOrder machinery — we no longer read it (the website is the
  // source of truth for ordering). Kept as a no-op so downstream refs are
  // safe.
  useEffect(() => { setModelOrder([]); }, [manufacturer?.id, deviceType?.id]);

  /** Fuzzy/token filter on the models list. Splits the query on whitespace and
   *  requires every token to appear anywhere in the label (case-insensitive)
   *  so "15 pro" and "pro 15" both match "iPhone 15 Pro Max". Now also
   *  searches the model_number ("A2848") and year ("2023") for Apple-style
   *  hunts. */
  const filteredModels = useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return modelsList;
    const tokens = q.split(/\s+/).filter(Boolean);
    return modelsList.filter((m) => {
      const hay = `${m.name} ${m.year} ${m.model_number}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [modelsList, modelQuery]);

  const makeLabel = manufacturer?.id === "other" ? (manufacturerOther || "Other") : manufacturer?.label;

  // Selected model — resolve back to the website record (for repairs +
  // part options). Uses the same normalized-alnum match the panel used
  // before, so slight whitespace/case drift doesn't kill the lookup.
  const selectedWebsiteModel = useMemo(() => {
    if (!selectedWebsiteDeviceType || !model) return null;
    if (modelId) {
      const exact = (selectedWebsiteDeviceType.models || []).find((m) => m.id === modelId);
      if (exact) return exact;
    }
    const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");
    const k = norm(model);
    return (selectedWebsiteDeviceType.models || []).find((m) => norm(m.name) === k) || null;
  }, [selectedWebsiteDeviceType, model, modelId]);

  const allProblems = useMemo(() => {
    const website = [];
    const modelKey = selectedWebsiteModel?.id || "";
    for (const rep of (selectedWebsiteModel?.repairs || [])) {
      const options = rep.part_options || [];
      website.push({
        id: `web_${modelKey}::${rep.id}`,
        _rawId: rep.id,
        _stockKey: `${modelKey}::${rep.id}`,
        label: rep.name,
        price: Number(options[0]?.price || 0),
        _website: true,
        _repairId: rep.id,
        _partOptions: options,
      });
    }
    // Locally-added catalog problems that scope to this manufacturer/
    // device type. Deduped against website by name.
    const websiteNames = new Set(website.map((x) => x.label.toLowerCase()));
    const currentDeviceId = deviceType?.id || "";
    const currentMfrId = manufacturer?.id || "";
    const currentModelId = modelId || "";
    const local = (catalog.problem || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .filter((c) => {
        const mfr = c.parent_manufacturer || "";
        const dev = c.parent_device_type || "";
        const mdl = c.parent_model || "";
        if (!mfr && (!dev || dev === "all")) return true;                     // global
        if (!mfr && dev) return dev === currentDeviceId;                       // device-scoped
        if (mfr && mfr !== currentMfrId) return false;
        if (dev && dev !== "all" && dev !== currentDeviceId) return false;
        if (mdl && mdl !== currentModelId) return false;
        return true;
      })
      .map((c) => ({
        id: `cat_${c.id}`,
        label: c.label,
        price: c.price || 0,
        _custom: true,
        entryId: c.id,
        parent_manufacturer: c.parent_manufacturer || "",
        parent_device_type: c.parent_device_type || "",
        parent_model: c.parent_model || "",
        hidden: !!c.hidden,
      }));
    const scope = (manufacturer?.id && deviceType?.id && selectedWebsiteModel?.id)
      ? `repairs:${manufacturer.id}:${deviceType.id}:${selectedWebsiteModel.id}` : "";
    const savedOrder = websiteOrders[scope] || [];
    // Arrange website repairs by canonical order (Screen Repair first),
    // honouring any per-model saved order. Same helper the Parts screen uses
    // so the two lists always match.
    const orderedWebsite = orderRepairs(website, { savedOrder, idOf: (r) => r._rawId });
    const base = [...orderedWebsite, ...local, ...customProblems];
    // Removals and the explicit per-model drag order set in Inventory →
    // Repair → Tree manager win last, so both screens show the same list.
    const mfrId = manufacturer?.id || "";
    const devId = deviceType?.id || "";
    const mdlId = selectedWebsiteModel?.id || "";
    return arrangeProblems(applyOrder(base, probOrder), {
      ...resolveRepairLayout(websiteOrders, mfrId, devId, mdlId),
      isCustom: (x) => !!x._custom,
      groupOf: (x) => (x._website ? REPAIR_GROUPS.website : x._custom ? REPAIR_GROUPS.custom : REPAIR_GROUPS.preset),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsiteModel, customProblems, probOrder, websiteOrders, manufacturer, deviceType, catalog.problem]);

  // Resolve the currently-priced amount for a given problem, honouring
  // (in priority order): explicit numeric override → selected part option
  // (multi-option website repairs) → the row's base price.
  const priceForProblem = (id) => {
    const p = allProblems.find((x) => x.id === id);
    const override = problemPrices[id];
    if (override !== undefined && override !== "") return Number(override);
    if (p?._website && Array.isArray(p._partOptions) && p._partOptions.length > 1) {
      const chosenId = problemPartOptionIds[id] || p._partOptions[0]?.id;
      const chosen = p._partOptions.find((o) => o.id === chosenId) || p._partOptions[0];
      return Number(chosen?.price || 0);
    }
    return Number(p?.price || 0);
  };

  const estimated = problems.reduce((sum, id) => sum + priceForProblem(id), 0);

  // Accessories subtotal (sum of unit_price × quantity). The checkout Total is
  // (repair estimate for current device + parked devices) + (accessories).
  const accessoriesTotal = accessories.reduce((s, a) => s + Number(a.unit_price || 0) * Number(a.quantity || 0), 0);
  const repairTotalAll = batchSubtotal + estimated;
  const checkoutTotal = repairTotalAll + accessoriesTotal;

  // Debounced product search — fires while the checkout "Add accessory" input
  // has text. Re-uses the existing /products endpoint (scoped by store).
  useEffect(() => {
    const q = accessorySearch.trim();
    if (q.length < 1 || step !== 5) { setAccessoryResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q, limit: 20 } });
        setAccessoryResults((data || []).slice(0, 8));
      } catch { setAccessoryResults([]); }
    }, 180);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessorySearch, step]);

  const addAccessory = (p) => {
    if (!p) return;
    if (Number(p.stock || 0) <= 0) { toast.error(`${p.name} is out of stock`); return; }
    setAccessories((arr) => {
      const existing = arr.find((a) => a.product_id === p.id);
      if (existing) {
        if (existing.quantity + 1 > Number(p.stock || 0)) { toast.error(`Only ${p.stock} in stock`); return arr; }
        return arr.map((a) => (a.product_id === p.id ? { ...a, quantity: a.quantity + 1 } : a));
      }
      return [
        ...arr,
        { product_id: p.id, sku: p.sku, name: p.name, unit_price: Number(p.price || 0), quantity: 1, stock: Number(p.stock || 0) },
      ];
    });
    setAccessorySearch("");
    setAccessoryResults([]);
    setAccessorySearchOpen(false);
  };
  const bumpAccessoryQty = (productId, delta) => {
    setAccessories((arr) =>
      arr
        .map((a) => {
          if (a.product_id !== productId) return a;
          const next = Math.max(0, a.quantity + delta);
          if (next > a.stock) { toast.error(`Only ${a.stock} in stock`); return a; }
          return { ...a, quantity: next };
        })
        .filter((a) => a.quantity > 0)
    );
  };
  const removeAccessory = (productId) =>
    setAccessories((arr) => arr.filter((a) => a.product_id !== productId));

  // Save an inline-edited LINE TOTAL. The input value represents what the
  // customer pays for the whole line (qty × unit). We derive the new
  // unit_price by dividing by the current quantity so qty steppers stay
  // correct. Empty/invalid input reverts silently.
  const commitEditPrice = (productId) => {
    const n = Number(editingPriceValue);
    if (editingPriceValue === "" || !isFinite(n) || n < 0) {
      setEditingPriceId("");
      setEditingPriceValue("");
      return;
    }
    setAccessories((arr) =>
      arr.map((a) => {
        if (a.product_id !== productId) return a;
        const qty = Math.max(1, Number(a.quantity || 1));
        return { ...a, unit_price: +(n / qty).toFixed(2) };
      })
    );
    setEditingPriceId("");
    setEditingPriceValue("");
  };

  const canNext =
    (step === 0 && manufacturer && (manufacturer.id !== "other" || manufacturerOther.trim())) ||
    (step === 1 && deviceType) ||
    (step === 2 && model.trim()) ||
    (step === 3 && problems.length > 0) ||
    (step === 4 && customerId && !showNewCust) ||
    step === 5;

  const selectedCustomer = customers.find((c) => c.id === customerId) || null;

  const addCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Name required");
    if (phoneMatches.length > 0) {
      return toast.error("A customer with this phone already exists. Use the existing customer or change the phone.");
    }
    try {
      const { data } = await api.post("/customers", newCust);
      // Populate the search with the new name so the redesigned picker
      // immediately renders the newly-created customer as a selected card
      // (search-first UX requires ≥2 chars; the name field is required and
      // always satisfies that). Staff can now edit / view / proceed without
      // landing on the empty-state prompt after save.
      setCustomers([data]);
      setCustomerId(data.id);
      setCustomerSearch((data.name || "").trim());
      setShowNewCust(false);
      setNewCust({ name: "", phone: "", email: "" });
      setPhoneMatches([]);
      toast.success(`Customer added · ${data.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyExistingCustomer = (c) => {
    setCustomers([c]);
    setCustomerId(c.id);
    setCustomerSearch((c.name || "").trim());
    setShowNewCust(false);
    setNewCust({ name: "", phone: "", email: "" });
    setPhoneMatches([]);
    toast.success(`Using existing customer · ${c.name}`);
  };

  // Persist catalog additions to backend + refresh + auto-select
  const saveCatalog = async (payload) => {
    try {
      const { data } = await api.post("/repair-catalog", payload);
      invalidateWizardCatalogCache();
      await loadCatalog({ force: true });
      toast.success("Saved to catalog");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const addMfr = async () => {
    const label = addMfrLabel.trim();
    if (!label) return toast.error("Enter a name");
    const saved = await saveCatalog({ type: "manufacturer", label });
    if (saved) {
      setManufacturer({ id: `cat_${saved.id}`, label: saved.label, _custom: true });
      setAddMfrOpen(false); setAddMfrLabel("");
    }
  };

  const addDev = async () => {
    const label = addDevLabel.trim();
    if (!label) return toast.error("Enter a name");
    const saved = await saveCatalog({ type: "device_type", label, parent_manufacturer: manufacturer?.id || "" });
    if (saved) {
      setDeviceType({ id: `cat_${saved.id}`, label: saved.label, _custom: true, entryId: saved.id });
      setAddDevOpen(false); setAddDevLabel("");
    }
  };

  const addModel = async (explicit) => {
    // Prefer the explicit argument (used by ADD MODEL / Enter key paths) so
    // we don't race the `setAddModelLabel` state commit. Fall back to state
    // for the legacy "Add a model" inline form.
    const label = ((typeof explicit === "string" ? explicit : addModelLabel) || "").trim();
    if (!label) return toast.error("Enter a model name");
    const saved = await saveCatalog({
      type: "model", label,
      parent_manufacturer: manufacturer?.id || "",
      parent_device_type: deviceType?.id || "",
    });
    if (saved) { setModel(saved.label); setModelId(`cat_${saved.id}`); setModelQuery(""); setAddModelOpen(false); setAddModelLabel(""); }
  };

  const brandEditorOptions = useMemo(
    () => allManufacturers.filter((m) => m.id !== "other").map((m) => ({ id: m.id, label: m.label })),
    [allManufacturers]
  );

  const getDeviceOptionsForBrand = (brandId) => {
    const fromWebsite = ((websiteCatalog?.manufacturers || [])
      .find((m) => m.id === brandId)?.device_types || [])
      .map((d) => ({ id: d.id, label: d.name }));
    const websiteNames = new Set(fromWebsite.map((d) => d.label.toLowerCase()));
    const local = (catalog.device_type || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .filter((c) => {
        const parent = c.parent_manufacturer || inferLocalDeviceTypeBrand(c.label);
        return !parent || parent === brandId;
      })
      .map((c) => ({ id: `cat_${c.id}`, label: c.label }));
    return [...fromWebsite, ...local].sort((a, b) => a.label.localeCompare(b.label));
  };

  const getModelOptionsForScope = (brandId, deviceId) => {
    const wMfr = (websiteCatalog?.manufacturers || []).find((m) => m.id === brandId);
    const wDev = (wMfr?.device_types || []).find((d) => d.id === deviceId);
    const fromWebsite = (wDev?.models || []).map((m) => ({ id: m.id, label: [m.name, m.year, m.model_number].filter(Boolean).join(" · ") }));
    const websiteNames = new Set((wDev?.models || []).map((m) => (m.name || "").toLowerCase()));
    const local = (catalog.model || [])
      .filter((c) => !c.override_of && !c.hidden && !websiteNames.has((c.label || "").toLowerCase()))
      .filter((c) => c.parent_manufacturer === brandId && c.parent_device_type === deviceId)
      .map((c) => ({ id: `cat_${c.id}`, label: c.label }));
    return [...fromWebsite, ...local].sort((a, b) => a.label.localeCompare(b.label));
  };

  const openCatalogEditor = (entry) => {
    const inferredBrand = entry.type === "device_type" ? inferLocalDeviceTypeBrand(entry.label) : "";
    const hasParentManufacturer = Object.prototype.hasOwnProperty.call(entry, "parent_manufacturer");
    const hasParentDeviceType = Object.prototype.hasOwnProperty.call(entry, "parent_device_type");
    const hasParentModel = Object.prototype.hasOwnProperty.call(entry, "parent_model");
    const nextMfr = hasParentManufacturer ? (entry.parent_manufacturer || "") : (inferredBrand || manufacturer?.id || "");
    const nextDev = hasParentDeviceType ? (entry.parent_device_type || "") : (entry.type !== "device_type" ? (deviceType?.id || "") : "");
    const nextModel = hasParentModel ? (entry.parent_model || "") : (entry.type === "problem" ? (modelId || "") : "");
    const scopeMode = entry.type === "device_type"
      ? (nextMfr ? "brand" : "all")
      : entry.type === "model"
        ? (!nextMfr && (!nextDev || nextDev === "all") ? "all" : "device")
        : (!nextMfr && (!nextDev || nextDev === "all") ? "all" : nextModel ? "model" : "device");
    setCatalogEditor({
      id: entry.entryId,
      type: entry.type,
      label: entry.label || entry.name || "",
      price: entry.price ?? 0,
      parent_manufacturer: nextMfr,
      parent_device_type: nextDev,
      parent_model: nextModel,
      scope_mode: scopeMode,
      hidden: !!entry.hidden,
      confirmDelete: false,
      current_brand_id: manufacturer?.id || "",
      current_device_type_id: deviceType?.id || "",
      current_model_id: modelId || "",
    });
  };

  const saveCatalogEditor = async () => {
    if (!catalogEditor?.id) return;
    const label = (catalogEditor.label || "").trim();
    if (!label) return toast.error("Enter a name");
    if (catalogEditor.type === "device_type" && catalogEditor.scope_mode === "brand" && !catalogEditor.parent_manufacturer) {
      return toast.error("Choose the brand for this device type");
    }
    if (catalogEditor.type === "model" && catalogEditor.scope_mode === "device" && (!catalogEditor.parent_manufacturer || !catalogEditor.parent_device_type)) {
      return toast.error("Choose where this model should appear");
    }
    if (catalogEditor.type === "problem" && catalogEditor.scope_mode === "device" && (!catalogEditor.parent_manufacturer || !catalogEditor.parent_device_type)) {
      return toast.error("Choose the device family for this problem");
    }
    if (catalogEditor.type === "problem" && catalogEditor.scope_mode === "model" && (!catalogEditor.parent_manufacturer || !catalogEditor.parent_device_type || !catalogEditor.parent_model)) {
      return toast.error("Choose the exact model for this problem");
    }
    const patch = {
      label,
      hidden: !!catalogEditor.hidden,
      parent_manufacturer: catalogEditor.scope_mode === "all" ? "" : (catalogEditor.parent_manufacturer || ""),
      parent_device_type: catalogEditor.type === "device_type"
        ? (catalogEditor.scope_mode === "all" ? "all" : "")
        : catalogEditor.scope_mode === "all"
          ? "all"
          : (catalogEditor.parent_device_type || ""),
      parent_model: catalogEditor.type === "problem" && catalogEditor.scope_mode === "model" ? (catalogEditor.parent_model || "") : "",
    };
    if (catalogEditor.type === "problem") patch.price = Number(catalogEditor.price || 0);
    try {
      setCatalogSaving(true);
      const { data } = await api.patch(`/repair-catalog/${catalogEditor.id}`, patch);
      invalidateWizardCatalogCache();
      const catId = `cat_${catalogEditor.id}`;
      if (catalogEditor.type === "device_type" && deviceType?.id === catId) {
        const stillInCurrentBrand = !data.parent_manufacturer || data.parent_manufacturer === manufacturer?.id;
        if (stillInCurrentBrand) setDeviceType({ id: catId, label: data.label, _custom: true, entryId: data.id, parent_manufacturer: data.parent_manufacturer || "" });
        else {
          setDeviceType(null);
          setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
          setProblems([]); setProblemPrices({});
        }
      }
      if (catalogEditor.type === "model" && (modelId === catId || model === catalogEditor.label)) {
        const stillInCurrentDevice = data.parent_manufacturer === manufacturer?.id && data.parent_device_type === deviceType?.id;
        if (stillInCurrentDevice) setModel(data.label);
        else {
          setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
          setProblems([]); setProblemPrices({});
        }
      }
      if (catalogEditor.type === "problem") {
        const mfrOk = !data.parent_manufacturer || data.parent_manufacturer === manufacturer?.id;
        const devOk = !data.parent_device_type || data.parent_device_type === "all" || data.parent_device_type === deviceType?.id;
        const modelOk = !data.parent_model || data.parent_model === modelId;
        if (!(mfrOk && devOk && modelOk)) {
          setProblems((prev) => prev.filter((id) => id !== catId));
          setProblemPrices((prev) => {
            const next = { ...prev };
            delete next[catId];
            return next;
          });
        }
      }
      await loadCatalog({ force: true });
      setCatalogEditor(null);
      toast.success("Wizard catalog updated");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save catalog item");
    } finally {
      setCatalogSaving(false);
    }
  };

  const deleteCatalogEditor = async () => {
    if (!catalogEditor?.id || !isAdmin) return;
    try {
      setCatalogSaving(true);
      await api.delete(`/repair-catalog/${catalogEditor.id}`);
      invalidateWizardCatalogCache();
      const catId = `cat_${catalogEditor.id}`;
      if (catalogEditor.type === "device_type" && deviceType?.id === catId) {
        setDeviceType(null);
        setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
        setProblems([]); setProblemPrices({}); setProblemNotes("");
      }
      if (catalogEditor.type === "model" && (modelId === catId || model === catalogEditor.label)) {
        setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
        setProblems([]); setProblemPrices({});
      }
      if (catalogEditor.type === "problem") {
        setProblems((prev) => prev.filter((id) => id !== catId));
        setProblemPrices((prev) => {
          const next = { ...prev };
          delete next[catId];
          return next;
        });
      }
      await loadCatalog({ force: true });
      setCatalogEditor(null);
      toast.success("Deleted from wizard catalog");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't delete");
    } finally {
      setCatalogSaving(false);
    }
  };

  const buildTicketPayload = () => {
    const issueLines = problems.map((id) => {
      const p = allProblems.find((x) => x.id === id);
      const price = priceForProblem(id);
      // For multi-option repairs (OEM / Soft / Incell), append the
      // chosen variant to the label so the intake receipt and the
      // repair ticket record which grade the customer paid for.
      let label = p?.label || "";
      if (p?._website && Array.isArray(p._partOptions) && p._partOptions.length > 1) {
        const chosenId = problemPartOptionIds[id] || p._partOptions[0]?.id;
        const chosen = p._partOptions.find((o) => o.id === chosenId);
        if (chosen) label = `${label} — ${chosen.label}`;
      }
      return `• ${label}${price ? ` ($${money(price)})` : ""}`;
    });
    // Don't bake the note into the issue string anymore — the backend
    // now accepts a `note` field that gets persisted as the FIRST
    // entry of the ticket's notes array (with author + timestamp),
    // visible in the ticket detail's Notes section. Keeps `issue`
    // clean as the problem summary for printing on the intake
    // receipt while still capturing the tech's note.
    // Include the model's year and body-code (e.g. "2020 A2337") from
    // the website catalog if we have them. Prints on receipts, invoices,
    // and workshop pickup slips so the tech has the exact revision at a
    // glance — critical for Apple where "MacBook Air 13"" spans 5+ years
    // of hardware.
    const modelParts = [model];
    if (modelMeta.year) modelParts.push(modelMeta.year);
    if (modelMeta.model_number) modelParts.push(modelMeta.model_number);
    const deviceModelFull = modelParts.filter(Boolean).join(" · ");

    return {
      customer_id: customerId,
      device_make: makeLabel,
      device_type: deviceType?.label || "",
      device_model: deviceModelFull,
      device_year: modelMeta.year || "",
      device_model_number: modelMeta.model_number || "",
      imei, passcode,
      issue: issueLines.join("\n"),
      estimated_cost: Number(estimated || 0),
      warranty_months: Number(warrantyMonths || 0),
      intake_checklist: problems.reduce((acc, id) => {
        const p = allProblems.find((x) => x.id === id);
        if (p?._website && Array.isArray(p._partOptions) && p._partOptions.length > 1) {
          const optId = problemPartOptionIds[id] || p._partOptions[0]?.id;
          acc[`web_${p._stockKey}::opt:${optId}`] = true;
        } else acc[id] = true;
        return acc;
      }, {}),
      note: problemNotes.trim(),
    };
  };

  // Snapshot the device currently being edited and push it onto the batch so
  // staff can start a fresh device for the same customer.
  const parkCurrentDevice = () => {
    if (!model.trim()) { toast.error("Pick a model first"); return false; }
    if (!problems.length && !customProblems.filter((cp) => cp.scope === "this").length) {
      toast.error("Pick at least one problem"); return false;
    }
    const base = buildTicketPayload();
    const entry = {
      uid: `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      ...base,
      _ui: {
        manufacturer, manufacturerOther, deviceType,
        customProblems: customProblems.filter((cp) => cp.scope === "this"),
        problems: [...problems],
        problem_prices: { ...problemPrices },
        problem_notes: problemNotes,
      },
    };
    setBatch((arr) => [...arr, entry]);
    // Reset only device-side state; keep customer, store, payment prefs,
    // accessories (they ride the combined invoice).
    setManufacturer(null); setManufacturerOther("");
    setDeviceType(null);
    setModel(""); setModelId(""); setModelMeta({ year: "", model_number: "" }); setModelQuery("");
    setImei(""); setPasscode("");
    setProblems([]); setProblemPrices({}); setProblemNotes("");
    setCustomProblems((arr) => arr.filter((cp) => cp.scope !== "this"));
    setStep(0); // jump back to Brand step for next device
    toast.success(`${base.device_make} ${base.device_model} added — next device?`);
    return true;
  };

  const removeBatchDevice = (uid) => {
    setBatch((arr) => arr.filter((d) => d.uid !== uid));
  };

  // Check if there is a device currently in-progress (i.e. user filled out
  // some device-side state after parking the previous one). Used to decide
  // whether the final "CHECKOUT" button should include the current screen.
  const hasCurrentDraft = !!(model.trim() || problems.length);

  // Fire-and-forget label print (doesn't block the success flow)
  const autoPrintLabel = async (ticketId, ticketNumber) => {
    // Respect: in-wizard toggle + global Settings toggle + printer selected + PrintNode configured
    if (!printOnCreate) return;
    if (!printConfigured) return;
    if (!prefs?.auto_print_label) return;
    if (!prefs?.label_printer_id) {
      toast("Label not auto-printed", {
        description: "Set a label printer in Settings → Printing to enable auto-print.",
      });
      return;
    }
    try {
      await api.post(`/print/label/${ticketId}`, {
        printer_id: prefs.label_printer_id,
        copies: 1,
      });
      toast.success(`Label sent to printer · ${ticketNumber}`);
    } catch (e) {
      toast.error(`Print failed: ${formatApiError(e)}`);
    }
  };

  // Fire-and-forget 80mm thermal customer pickup slip (TICKET ONLY path)
  const autoPrintTicketSlip = async (ticketId, ticketNumber) => {
    if (!printConfigured) return;
    if (!prefs?.auto_print_receipt) return;
    if (!prefs?.receipt_printer_id) return;
    try {
      await api.post(`/print/ticket-slip/${ticketId}`, {
        printer_id: prefs.receipt_printer_id,
        copies: 1,
      });
      toast.success(`Pickup slip sent to printer · ${ticketNumber}`);
    } catch (e) {
      toast.error(`Slip print failed: ${formatApiError(e)}`);
    }
  };

  // Fire-and-forget 80mm thermal sale receipt (PAY & CREATE TICKET path)
  const autoPrintSaleReceipt = async (saleId, saleNumber) => {
    if (!printConfigured) return;
    if (!prefs?.auto_print_receipt) return;
    if (!prefs?.receipt_printer_id) return;
    try {
      await api.post(`/print/receipt/${saleId}`, {
        printer_id: prefs.receipt_printer_id,
        copies: 1,
      });
      toast.success(`Receipt sent to printer · ${saleNumber}`);
    } catch (e) {
      toast.error(`Receipt print failed: ${formatApiError(e)}`);
    }
  };

  const submitTicketOnly = async () => {
    if (!customerId) return toast.error("Select a customer");
    setSubmitting(true);
    try {
      // If there's a batch, use the batch endpoint (no sale) so every device
      // becomes a ticket. Otherwise take the single-device fast path.
      const currentPayload = hasCurrentDraft ? buildTicketPayload() : null;
      const allDevices = [
        ...batch.map((b) => ({
          device_make: b.device_make, device_type: b.device_type, device_model: b.device_model, device_year: b.device_year, device_model_number: b.device_model_number,
          imei: b.imei, passcode: b.passcode, issue: b.issue,
          estimated_cost: Number(b.estimated_cost || 0),
          warranty_months: Number(b.warranty_months || 0),
          intake_checklist: b.intake_checklist || {},
          note: b.note || "",
        })),
        ...(currentPayload
          ? [{
              device_make: currentPayload.device_make,
              device_type: currentPayload.device_type,
              device_model: currentPayload.device_model,
              device_year: currentPayload.device_year,
              device_model_number: currentPayload.device_model_number,
              imei: currentPayload.imei, passcode: currentPayload.passcode,
              issue: currentPayload.issue,
              estimated_cost: currentPayload.estimated_cost,
              warranty_months: currentPayload.warranty_months,
              intake_checklist: currentPayload.intake_checklist,
            note: currentPayload.note || "",
            }]
          : []),
      ];
      if (!allDevices.length) { toast.error("Add at least one device"); return; }

      if (allDevices.length === 1 && !batch.length) {
        // Single-device legacy fast path
        const payload = buildTicketPayload();
        // Persist accessories captured at intake so the later "Charge at
        // POS" checkout auto-populates them — cashier doesn't re-key.
        if (accessories.length > 0) {
          payload.accessories = accessories.map((a) => ({
            product_id: a.product_id,
            sku: a.sku,
            name: a.name,
            quantity: a.quantity,
            unit_price: Number(a.unit_price || 0),
            line_total: Number(a.unit_price || 0) * Number(a.quantity || 0),
            kind: "product",
          }));
        }
        const { data } = await api.post("/repair-tickets", payload);
        toast.success(`Ticket ${data.ticket_number} created`);
        autoPrintLabel(data.id, data.ticket_number);
        autoPrintTicketSlip(data.id, data.ticket_number);
        onCreated?.(data);
        // Open the post-ticket actions popup so staff can immediately print /
        // SMS / email / edit. Wizard stays mounted underneath so the popup's
        // parent chain doesn't unmount mid-interaction.
        const custObj = customers.find((c) => c.id === customerId) || { id: customerId };
        setPostTicket({ ticket: data, tickets: [data], customer: custObj });
        return;
      }
      const { data } = await api.post("/repair-tickets/batch", {
        customer_id: customerId,
        tickets: allDevices,
        // Persist any accessories from the intake wizard so they auto-
        // populate at future checkout — ticket-only path doesn't create
        // a sale yet, but we don't want cashiers re-keying case + screen
        // protector later.
        ...(accessories.length > 0
          ? {
              accessories: accessories.map((a) => ({
                product_id: a.product_id,
                sku: a.sku,
                name: a.name,
                quantity: a.quantity,
                unit_price: Number(a.unit_price || 0),
                line_total: Number(a.unit_price || 0) * Number(a.quantity || 0),
                kind: "product",
              })),
            }
          : {}),
      }, { headers: { "Idempotency-Key": getCheckoutIdemKey() } });
      const tickets = data.tickets || [];
      const created = tickets[0];
      // The /batch endpoint now consolidates every device into ONE ticket
      // (returned as a 1-element array). Tell staff "Ticket R-XXXX · N devices"
      // instead of the legacy "N tickets created" — clearer and accurate.
      toast.success(
        allDevices.length > 1
          ? `Ticket ${created?.ticket_number || ""} · ${allDevices.length} devices`
          : `Ticket ${created?.ticket_number || ""}`
      );
      // One label + one ticket slip per ticket (1 ticket now, however many devices).
      tickets.forEach((t) => {
        autoPrintLabel(t.id, t.ticket_number);
        autoPrintTicketSlip(t.id, t.ticket_number);
      });
      onCreated?.(created);
      const custObj = customers.find((c) => c.id === customerId) || { id: customerId };
      setPostTicket({ ticket: created, tickets, customer: custObj });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitCheckout = async (tyroMeta = null) => {
    if (!customerId) return toast.error("Select a customer");

    // ——— Collect every device that should be part of this intake ———
    // `batch` = devices already parked; the currently-visible wizard screen
    // also counts if it has any real data in it. We always merge them into a
    // single `allDevices` array so the final payload is predictable.
    const currentPayload = hasCurrentDraft ? buildTicketPayload() : null;
    if (currentPayload && !currentPayload.device_model) {
      return toast.error("Finish the current device or remove it before checkout");
    }
    const allDevices = [
      ...batch.map((b) => ({
        device_make: b.device_make, device_type: b.device_type, device_model: b.device_model, device_year: b.device_year, device_model_number: b.device_model_number,
        imei: b.imei, passcode: b.passcode, issue: b.issue,
        estimated_cost: Number(b.estimated_cost || 0),
        warranty_months: Number(b.warranty_months || 0),
        intake_checklist: b.intake_checklist || {},
          note: b.note || "",
      })),
      ...(currentPayload
        ? [{
            device_make: currentPayload.device_make,
              device_type: currentPayload.device_type,
            device_model: currentPayload.device_model,
              device_year: currentPayload.device_year,
              device_model_number: currentPayload.device_model_number,
            imei: currentPayload.imei, passcode: currentPayload.passcode,
            issue: currentPayload.issue,
            estimated_cost: currentPayload.estimated_cost,
            warranty_months: currentPayload.warranty_months,
            intake_checklist: currentPayload.intake_checklist,
            note: currentPayload.note || "",
          }]
        : []),
    ];
    if (!allDevices.length) return toast.error("Add at least one device");

    const repairTotal = allDevices.reduce((s, d) => s + Number(d.estimated_cost || 0), 0);
    // Resolve the tender source: split-mode sums per-method amounts; otherwise
    // the single Amount-tendered field is used. Both feed the same payload.
    const splitCashNum2 = Number(splitCash || 0);
    const splitCardNum2 = Number(splitCard || 0);
    const splitTyroNum2 = Number(splitTyro || 0);
    const splitOtherNum2 = Number(splitOther || 0);
    // Stripe split slice is intentionally excluded from tenderedNum — it
    // isn't collected now, it's sent as a payment link after the ticket
    // is created (see the `data.tickets[0]` handling below).
    const splitStripeNum2 = splitMode ? Number(splitStripe || 0) : 0;
    const tenderedNum = collectPayment
      ? (splitMode
          ? splitCashNum2 + splitCardNum2 + splitTyroNum2 + splitOtherNum2
          : Number(tendered || 0))
      : 0;
    // Combined taxable amount = all repairs + any accessories
    const combinedGross = repairTotal + accessoriesTotal;
    const taxRate = Number(currentStore?.tax_rate || 0) / 100;
    const taxInclusive = currentStore?.tax_inclusive !== false;
    const taxAmount = taxRate > 0
      ? (taxInclusive ? combinedGross - (combinedGross / (1 + taxRate)) : combinedGross * taxRate)
      : 0;
    const saleTotal = taxInclusive ? combinedGross : combinedGross + taxAmount;

    // Tyro gating — when paymentMethod is "tyro" (single tender) and we
    // haven't already approved the terminal transaction, pop the dialog
    // for the FULL sale total; on approval the dialog re-enters this
    // function with `tyroMeta` set, which routes through the card_manual
    // branch on the server. Split-mode Tyro is handled below per-slice.
    // Server-check the accessories BEFORE the terminal is charged, so an
    // approved card can't be left with a sale the server rejects.
    const precheckBeforeCharge = async () => {
      try {
        await api.post("/sales/precheck", {
          items: accessories.map((a) => ({ product_id: a.product_id, name: a.name, quantity: Number(a.quantity || 1), kind: "product" })),
        });
        return true;
      } catch (e) {
        toast.error(`${formatApiError(e)} — nothing was charged`);
        return false;
      }
    };
    if (!tyroMeta && collectPayment && !splitMode && paymentMethod === "tyro") {
      if (saleTotal <= 0) return toast.error("Total must be greater than $0 to charge Tyro");
      if (!(await precheckBeforeCharge())) return;
      setTyroSaveError(null);
      setTyroOpen(true);
      return;
    }
    if (!tyroMeta && collectPayment && splitMode && splitTyroNum2 > 0) {
      if (splitTyroNum2 > saleTotal + 0.01) {
        return toast.error(`Tyro slice ($${splitTyroNum2.toFixed(2)}) exceeds total ($${saleTotal.toFixed(2)})`);
      }
      if (!(await precheckBeforeCharge())) return;
      setTyroSaveError(null);
      setTyroSplitOpen(true);
      return;
    }
    if (tyroMeta) lastTyroMetaRef.current = tyroMeta;

    // Partial payments are allowed — same UX as POS checkout. We just require
    // *some* tender so we don't silently create a zero-paid invoice by
    // mistake; the customer can top-up later from the invoice page.
    // EXCEPTION: single-tender Tyro skips this check because the SDK has
    // already charged the full sale total — `tendered` is intentionally
    // empty in that flow, and the wizard's amount_tendered for the sale
    // is set to `saleTotal` below (`isSingleTyro ? saleTotal : ...`).
    // Without this exception the wizard would block AFTER the customer's
    // card was charged on the terminal — orphaning the Tyro auth and
    // leaving the cashier stuck on the Finish screen.
    const isSingleTyroEarly = !splitMode && paymentMethod === "tyro" && !!tyroMeta;
    // A Stripe split slice explains the "shortfall" on its own — it's
    // going out as a payment link, not sitting uncollected by accident.
    // ANY stripe amount qualifies here, not just one that happens to cover
    // the rest of the total — a $2 deposit on a $119 repair with nothing
    // else tendered is exactly the deposit-to-order-parts flow this was
    // built for, and must not be blocked as "no amount entered".
    const hasStripeSlice = splitMode && splitStripeNum2 > 0;
    if (!isSingleTyroEarly && !hasStripeSlice && saleTotal > 0 && tenderedNum <= 0) {
      return toast.error("Enter an amount tendered (or use EXACT for a full payment)");
    }
    const balanceDue = Math.max(0, saleTotal - tenderedNum);
    if (!tyroMeta && !hasStripeSlice && tenderedNum + 1e-6 < saleTotal) {
      const ok = window.confirm(
        `Take a deposit of $${tenderedNum.toFixed(2)}?\n\n` +
        `Balance due: $${balanceDue.toFixed(2)}\n\n` +
        `The customer can settle the balance later from the invoice page.`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      // One combined batch call → N tickets + 1 sale server-side
      const extraItems = accessories.map((a) => ({
        product_id: a.product_id,
        sku: a.sku,
        name: a.name,
        quantity: a.quantity,
        unit_price: Number(a.unit_price || 0),
        line_total: Number(a.unit_price || 0) * Number(a.quantity || 0),
        kind: "product",
      }));
      // Single-tender Tyro → server enum doesn't allow "tyro" for
      // `payment_method`; we collapse to card_manual + a `tyro_meta`
      // sidecar that carries auth_code, RRN, card scheme & elided PAN
      // so refunds can be linked back to the terminal transaction.
      const isSingleTyro = !splitMode && paymentMethod === "tyro" && !!tyroMeta;
      // The sale's headline `payment_method` only accepts cash|card_manual|other.
      // Stripe (deferred payment link) and Tyro (real-time terminal) are NOT
      // valid headline tenders, so sending them verbatim 422s the whole batch —
      // which left the ticket orphaned with no sale, no invoice and no Stripe
      // link (exactly the "stripe split creates nothing" bug). In split mode we
      // derive the headline from the actual in-store slice; otherwise coerce any
      // stripe/tyro selection to a safe enum value.
      let serverPaymentMethod;
      if (isSingleTyro) {
        serverPaymentMethod = "card_manual";
      } else if (splitMode) {
        serverPaymentMethod = splitCashNum2 > 0 ? "cash"
          : splitCardNum2 > 0 ? "card_manual"
          : splitOtherNum2 > 0 ? "other"
          : "cash"; // stripe-only / tyro-only split → unpaid sale, link/terminal settles it
      } else {
        serverPaymentMethod = (paymentMethod === "stripe" || paymentMethod === "tyro")
          ? "card_manual"
          : paymentMethod;
      }
      const sale_payload = {
        items_extra: extraItems,
        tax: Number(taxAmount.toFixed(2)),
        payment_method: serverPaymentMethod,
        amount_tendered: isSingleTyro ? saleTotal : tenderedNum,
        // Send `payments[]` only when split-tender is on so the legacy
        // single-method path stays untouched for staff who don't split.
        ...(collectPayment && splitMode
          ? {
              payments: [
                splitCashNum2 > 0 && { amount: splitCashNum2, method: "cash" },
                splitCardNum2 > 0 && { amount: splitCardNum2, method: "card_manual" },
                splitTyroNum2 > 0 && {
                  amount: splitTyroNum2,
                  method: "tyro",
                  ...(tyroMeta ? { tyro_meta: tyroMeta } : {}),
                },
                splitOtherNum2 > 0 && { amount: splitOtherNum2, method: "other" },
              ].filter(Boolean),
            }
          : {}),
        ...(isSingleTyro ? { tyro_meta: tyroMeta } : {}),
        // No auto-note — the user complained these "Batch intake · N
        // device(s)" lines clutter the customer-facing thermal receipt.
        // If a cashier wants a note on the sale they can add it later
        // from the invoice page; the multi-device summary is already
        // visible in the sale line items.
      };
      const { data } = await api.post("/repair-tickets/batch", {
        customer_id: customerId,
        tickets: allDevices,
        sale: sale_payload,
      }, { headers: { "Idempotency-Key": getCheckoutIdemKey() } });
      const tickets = data.tickets || [];
      const sale = data.sale;
      // /batch now returns ONE consolidated ticket regardless of device count
      // — surface that to the cashier so they don't expect 3 ticket numbers.
      toast.success(
        allDevices.length > 1
          ? `Ticket ${tickets[0]?.ticket_number} · ${allDevices.length} devices + Sale ${sale?.sale_number || ""}`
          : `Ticket ${tickets[0]?.ticket_number} + Sale ${sale?.sale_number || ""}`
      );
      // Auto-print one label per ticket + one combined receipt (1 ticket now).
      tickets.forEach((t) => autoPrintLabel(t.id, t.ticket_number));
      if (sale) autoPrintSaleReceipt(sale.id, sale.sale_number);
      onCreated?.(tickets[0]);

      // Stripe split slice — the remainder (or the whole total, if no
      // in-store slice was entered) goes out as a payment link right away.
      // ensure_invoice_for_ticket already ran synchronously server-side
      // during checkout, so the invoice exists the moment we look it up.
      if (splitStripeNum2 > 0 && tickets[0]?.id) {
        try {
          const { data: invList } = await api.get(`/online-invoices/by-ticket/${tickets[0].id}`);
          const invoice = (invList.items || [])[0];
          const cust = customers.find((c) => c.id === customerId);
          const hasContact = cust?.phone || cust?.email;
          if (invoice && hasContact) {
            // Requests a Stripe link for exactly splitStripeNum2 (a deposit,
            // if it's less than the invoice's full remaining balance) — not
            // the full amount_due, so any leftover stays open on the invoice.
            await api.post(`/online-invoices/${invoice.id}/stripe-link`, {
              amount: splitStripeNum2,
              send_via: "both",
            });
            toast.success(`Stripe deposit link for $${splitStripeNum2.toFixed(2)} sent to ${cust.name || "customer"}`);
          } else if (invoice) {
            toast.warning("Add a phone or email on this customer to send the Stripe link");
          }
        } catch (e) {
          toast.error("Ticket created, but the Stripe link couldn't be sent — send it from the invoice page");
        }
      }

      const customerObj = customers.find((c) => c.id === customerId) || { id: customerId };
      setTyroSaveError(null);
      setPostSale({ sale, customer: customerObj, ticket: tickets[0], tickets, stripeDepositAmount: splitStripeNum2 > 0 ? splitStripeNum2 : 0 });
    } catch (e) {
      const st = e?.response?.status;
      if (tyroMeta && (!st || st >= 500)) throw e; // Tyro dialog auto-retries
      toast.error(formatApiError(e));
      if (tyroMeta) setTyroSaveError(`${formatApiError(e)} — the card WAS charged. Fix the issue and tap Retry save; don't charge again.`);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Stripe invoice flow ────────────────────────────────────────────
     Creates the repair ticket(s) first (no in-store sale), then fires
     off an online invoice via /api/online-invoices and sends the payment
     link to the customer automatically. */
  const submitStripeInvoice = async () => {
    if (!customerId) return toast.error("Select a customer");
    if (submitting) return; // Prevent double-submit
    setSubmitting(true);
    try {
      // 1. Create ticket(s) — same as submitTicketOnly
      const currentPayload = hasCurrentDraft ? buildTicketPayload() : null;
      const allDevices = [
        ...batch.map((b) => ({
          device_make: b.device_make, device_type: b.device_type, device_model: b.device_model, device_year: b.device_year, device_model_number: b.device_model_number,
          imei: b.imei, passcode: b.passcode, issue: b.issue,
          estimated_cost: Number(b.estimated_cost || 0),
          warranty_months: Number(b.warranty_months || 0),
          intake_checklist: b.intake_checklist || {},
          note: b.note || "",
        })),
        ...(currentPayload
          ? [{
              device_make: currentPayload.device_make,
              device_type: currentPayload.device_type,
              device_model: currentPayload.device_model,
              device_year: currentPayload.device_year,
              device_model_number: currentPayload.device_model_number,
              imei: currentPayload.imei, passcode: currentPayload.passcode,
              issue: currentPayload.issue,
              estimated_cost: currentPayload.estimated_cost,
              warranty_months: currentPayload.warranty_months,
              intake_checklist: currentPayload.intake_checklist,
            note: currentPayload.note || "",
            }]
          : []),
      ];
      if (!allDevices.length) { toast.error("Add at least one device"); return; }

      let tickets, created;
      if (allDevices.length === 1 && !batch.length) {
        const payload = buildTicketPayload();
        if (accessories.length > 0) {
          payload.accessories = accessories.map((a) => ({
            product_id: a.product_id, sku: a.sku, name: a.name,
            quantity: a.quantity, unit_price: Number(a.unit_price || 0),
            line_total: Number(a.unit_price || 0) * Number(a.quantity || 0),
            kind: "product",
          }));
        }
        const { data } = await api.post("/repair-tickets", payload);
        created = data;
        tickets = [data];
      } else {
        const { data } = await api.post("/repair-tickets/batch", {
          customer_id: customerId,
          tickets: allDevices,
          ...(accessories.length > 0 ? {
            accessories: accessories.map((a) => ({
              product_id: a.product_id, sku: a.sku, name: a.name,
              quantity: a.quantity, unit_price: Number(a.unit_price || 0),
              line_total: Number(a.unit_price || 0) * Number(a.quantity || 0),
              kind: "product",
            })),
          } : {}),
        }, { headers: { "Idempotency-Key": getCheckoutIdemKey() } });
        tickets = data.tickets || [];
        created = tickets[0];
      }
      toast.success(`Ticket ${created?.ticket_number || ""} created`);
      tickets.forEach((t) => {
        autoPrintLabel(t.id, t.ticket_number);
        autoPrintTicketSlip(t.id, t.ticket_number);
      });
      onCreated?.(created);

      // 2. Build invoice line items from all devices' repairs
      const invoiceItems = [];
      allDevices.forEach((dev) => {
        const lines = (dev.issue || "").split("\n").map((l) => l.replace(/^[•\-\*]\s*/, "").trim()).filter(Boolean);
        if (lines.length > 0) {
          const perLine = lines.length > 0 ? (dev.estimated_cost || 0) / lines.length : 0;
          lines.forEach((l) => invoiceItems.push({ description: l, quantity: 1, unit_price: Math.round(perLine * 100) / 100 }));
        } else {
          invoiceItems.push({
            description: `Repair — ${dev.device_make || ""} ${dev.device_model || ""}`.trim(),
            quantity: 1, unit_price: dev.estimated_cost || 0,
          });
        }
      });
      // Add accessories
      accessories.forEach((a) => {
        invoiceItems.push({ description: a.name, quantity: a.quantity || 1, unit_price: Number(a.unit_price || 0) });
      });

      // 3. Send the online invoice
      const cust = customers.find((c) => c.id === customerId);
      const hasContact = cust?.phone || cust?.email;
      try {
        const { data: inv } = await api.post("/online-invoices", {
          customer_id: customerId,
          customer_name: cust?.name || created?.customer_name || "Customer",
          customer_email: cust?.email || "",
          customer_phone: cust?.phone || "",
          items: invoiceItems,
          note: `Repair for ${allDevices.map((d) => `${d.device_make || ""} ${d.device_model || ""}`.trim()).join(", ")}`,
          due_days: 7,
          linked_ticket_id: created?.id,
          send_via: hasContact ? "both" : null,
          device_make: makeLabel || allDevices[0]?.device_make || "",
          device_type: deviceType?.label || "",
          device_model: model || allDevices[0]?.device_model || "",
        });
        // Show notification errors if any
        if (inv.notification?.errors?.length) {
          inv.notification.errors.forEach((e) => toast.error(e));
        }
        if (hasContact) {
          toast.success(`Invoice ${inv.invoice_number} sent to customer`);
        } else {
          const payLink = `${window.location.origin}/pay/${inv.payment_token}`;
          navigator.clipboard.writeText(payLink).catch(() => {});
          toast.success(`Invoice ${inv.invoice_number} created — payment link copied!`);
        }
      } catch (invErr) {
        // Ticket was already created — don't fail the whole flow
        toast.error("Ticket created, but invoice sending failed: " + formatApiError(invErr));
      }

      const custObj = customers.find((c) => c.id === customerId) || { id: customerId };
      setPostTicket({ ticket: created, tickets, customer: custObj });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open && !postSale && !postTicket) return null;

  const progress = ((step) / (STEPS.length - 1)) * 100;

  const handlePostSaleClose = () => {
    setPostSale(null);
    onClose?.();
  };

  return (
    <>
    {open && !postSale && (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/60 flex items-center justify-center p-2 sm:p-5"
      data-testid="wizard-overlay"
    >
      <div
        className="isolate bg-white w-full max-w-6xl max-h-[95vh] rounded-[26px] shadow-2xl shadow-slate-950/30 border border-slate-200 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-ticket-wizard"
      >
        {/* Header */}
        <div className="relative bg-white border-b border-slate-200">
          <div className="flex items-center justify-between gap-3 px-5 sm:px-7 py-4">
            <div className="min-w-0 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm shrink-0">
                <Wrench size={17} weight="bold" className="text-[#1E5FEB]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">STEP {step + 1} OF {STEPS.length}</div>
                <h2 className="font-heading font-bold text-[19px] sm:text-[22px] tracking-tight text-slate-950 leading-tight truncate">
                  New repair ticket
                  <span className="hidden sm:inline text-slate-400 font-normal"> · {STEPS[step].label}</span>
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0"
              data-testid="wizard-close"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Sleek progress bar */}
        <div className="px-5 sm:px-7 py-3 bg-[#FAFBFC] border-b border-slate-200 shrink-0">
          <div className="relative h-1 bg-slate-200 rounded-full overflow-hidden" data-testid="wizard-progress-track">
            <div
              className="absolute inset-y-0 left-0 bg-[#1E5FEB] rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="wizard-progress-fill"
            />
          </div>
          <div className="flex justify-between mt-3 gap-1">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                disabled={i > step}
                onClick={() => i < step && setStep(i)}
                className={`flex flex-col items-center gap-1.5 group min-w-0 ${i > step ? "cursor-default" : "cursor-pointer"}`}
                data-testid={`wizard-step-${s.key}`}
              >
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-[transform,background-color,border-color,color] duration-300 ${
                    i < step
                      ? "bg-[#1E5FEB] text-white scale-100 group-hover:scale-105"
                      : i === step
                      ? "bg-white border-2 border-[#1E5FEB] text-[#1E5FEB] scale-110"
                      : "bg-slate-100 text-slate-400 scale-95"
                  }`}
                >
                  {i < step ? <Check size={13} weight="bold" /> : i + 1}
                </span>
                <span
                  className={`text-[10px] font-semibold tracking-wide transition-colors truncate max-w-[70px] sm:max-w-none ${
                    i === step ? "text-slate-900" : i < step ? "text-slate-600 group-hover:text-[#1E5FEB]" : "text-slate-400"
                  }`}
                >
                  {s.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Batch strip — lists devices already parked for this customer.
            Appears once the user clicks "+ Add another device" on checkout. */}
        {batch.length > 0 && (
          <div
            className="mx-7 mb-4 rounded-xl border px-3 py-2.5 flex items-center gap-2 overflow-x-auto"
            style={{
              background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
              borderColor: "#BFDBFE",
            }}
            data-testid="wizard-batch-strip"
          >
            <div className="shrink-0 text-[10px] tracking-[0.14em] uppercase font-bold text-[#1E40AF] pr-1 border-r border-blue-200 mr-1">
              Intake · {batch.length + (hasCurrentDraft ? 1 : 0)}
            </div>
            {batch.map((d) => (
              <div
                key={d.uid}
                data-testid={`wizard-batch-chip-${d.uid}`}
                className="shrink-0 flex items-center gap-1.5 bg-white border border-blue-200 rounded-full pl-2.5 pr-1 py-1"
              >
                <Check size={10} weight="bold" className="text-emerald-600" />
                <span className="text-[11px] font-semibold text-slate-800 truncate max-w-[140px]">
                  {d.device_make} {d.device_model}
                </span>
                <span className="text-[10px] font-mono text-slate-500">${money(d.estimated_cost || 0)}</span>
                <button
                  type="button"
                  onClick={() => removeBatchDevice(d.uid)}
                  className="w-4 h-4 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  title="Remove"
                  data-testid={`wizard-batch-remove-${d.uid}`}
                >
                  <X size={9} weight="bold" />
                </button>
              </div>
            ))}
            {hasCurrentDraft && (
              <div className="shrink-0 flex items-center gap-1.5 bg-white/70 border border-dashed border-blue-300 rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1E5FEB] animate-pulse" />
                <span className="text-[11px] font-semibold text-[#1E40AF]">In progress…</span>
              </div>
            )}
            <div className="shrink-0 ml-auto text-[11px] font-mono font-bold text-[#1E40AF] pr-1">
              ${money(batchSubtotal)}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto px-4 sm:px-7 pt-6 pb-28 bg-[#FAFBFC]">
          {step === 0 && (
            <Step title="Which brand?" subtitle="Pick the manufacturer. Choose Other if it's not listed.">
              <WiggleToolbar
                count={allManufacturers.length}
                reorder={mfrReorder}
                setReorder={setMfrReorder}
                onDone={async () => {
                  try {
                    await api.put("/catalog/website/order", {
                      scope: "manufacturers",
                      order: allManufacturers.filter((x) => x.id !== "other").map((x) => x.id),
                    });
                    toast.success("Order saved");
                  } catch (e) { toast.error(formatApiError(e) || "Couldn't save order"); }
                  setMfrReorder(false);
                }}
                testidPrefix="mfr"
              />
              <div className={`grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 ${mfrReorder ? "rh-wiggle" : ""}`} data-testid="wizard-manufacturer-grid">
                {allManufacturers.map((m, idx) => (
                  <div key={m.id} className="relative">
                    <OptionCard
                      selected={!mfrReorder && manufacturer?.id === m.id}
                      onClick={() => { if (!mfrReorder) setManufacturer(m); }}
                      testid={`mfr-${m.id}`}
                      draggable={mfrReorder && m.id !== "other"}
                      dragClass={mfrReorder ? "cursor-grab active:cursor-grabbing" : ""}
                      onDragStart={() => setMfrDragIdx(idx)}
                      onDragOver={(e) => {
                        if (!mfrReorder || mfrDragIdx === null || mfrDragIdx === idx) return;
                        e.preventDefault();
                        // Rearrange the ids array kept in websiteOrders so
                        // the memo picks up the change immediately.
                        const currentIds = allManufacturers.filter((x) => x.id !== "other").map((x) => x.id);
                        const [moved] = currentIds.splice(mfrDragIdx, 1);
                        currentIds.splice(idx, 0, moved);
                        setWebsiteOrders((prev) => ({ ...prev, manufacturers: currentIds }));
                        setMfrDragIdx(idx);
                      }}
                      onDragEnd={() => setMfrDragIdx(null)}
                    >
                      <div className="w-full flex flex-col items-center gap-2">
                        {mfrReorder && (
                          <div className="text-[9px] font-mono text-slate-400 tabular-nums select-none">#{idx + 1}</div>
                        )}
                        {m.id !== "other" ? (
                          <BrandAvatar id={m.id} label={m.label} />
                        ) : (
                          <div className="w-11 h-11 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                            <Plus size={16} weight="bold" />
                          </div>
                        )}
                        <div className="text-[13px] font-semibold text-center w-full leading-tight">
                          {m.label}
                        </div>
                      </div>
                    </OptionCard>
                  </div>
                ))}
                {!addMfrOpen && !mfrReorder && (
                  <button
                    type="button"
                    onClick={() => setAddMfrOpen(true)}
                    data-testid="wizard-add-mfr"
                  className="min-h-[104px] flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border border-dashed border-slate-300 hover:border-[#1E5FEB] hover:bg-slate-50 text-slate-500 hover:text-[#1E5FEB] transition-colors"
                  >
                    <Plus size={18} weight="bold" />
                    <span className="text-[11px] font-bold tracking-wider">ADD NEW</span>
                  </button>
                )}
              </div>
              {addMfrOpen && (
                <InlineAdd
                  label="New manufacturer"
                  placeholder="e.g. Fairphone"
                  value={addMfrLabel}
                  onChange={setAddMfrLabel}
                  onSave={addMfr}
                  onCancel={() => { setAddMfrOpen(false); setAddMfrLabel(""); }}
                  testid="wizard-add-mfr-form"
                />
              )}
              {manufacturer?.id === "other" && (
                <div className="mt-5">
                  <Label>Custom manufacturer (this ticket only)</Label>
                  <input
                    value={manufacturerOther}
                    onChange={(e) => setManufacturerOther(e.target.value)}
                    placeholder="e.g. Fairphone"
                    className={inputCls + " max-w-md"}
                    data-testid="mfr-other-input"
                  />
                </div>
              )}
            </Step>
          )}

          {step === 1 && (
            <Step title="Device type" subtitle={`For ${makeLabel}`}>
              <WiggleToolbar
                count={allDeviceTypes.length}
                reorder={devReorder}
                setReorder={setDevReorder}
                onDone={async () => {
                  try {
                    await api.put("/catalog/website/order", {
                      scope: `device_types:${manufacturer?.id}`,
                      order: allDeviceTypes.filter((x) => x.id !== "other").map((x) => x.id),
                    });
                    toast.success("Order saved");
                  } catch (e) { toast.error(formatApiError(e) || "Couldn't save order"); }
                  setDevReorder(false);
                }}
                testidPrefix="dev"
              />
              <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 ${devReorder ? "rh-wiggle" : ""}`} data-testid="wizard-device-grid">
                {allDeviceTypes.map((d, idx) => {
                  const Icon = resolveDeviceIcon(d.id, d.label);
                  const sel = !devReorder && deviceType?.id === d.id;
                  return (
                    <div key={d.id} className="relative">
                      <OptionCard
                        selected={sel}
                        onClick={() => { if (!devReorder) setDeviceType(d); }}
                        testid={`dev-${d.id}`}
                        draggable={devReorder && d.id !== "other"}
                        dragClass={devReorder ? "cursor-grab active:cursor-grabbing" : ""}
                        onDragStart={() => setDevDragIdx(idx)}
                        onDragOver={(e) => {
                          if (!devReorder || devDragIdx === null || devDragIdx === idx) return;
                          e.preventDefault();
                          const currentIds = allDeviceTypes.filter((x) => x.id !== "other").map((x) => x.id);
                          const [moved] = currentIds.splice(devDragIdx, 1);
                          currentIds.splice(idx, 0, moved);
                          setWebsiteOrders((prev) => ({ ...prev, [`device_types:${manufacturer?.id}`]: currentIds }));
                          setDevDragIdx(idx);
                        }}
                        onDragEnd={() => setDevDragIdx(null)}
                      >
                        <Icon size={30} weight="duotone" className={`mb-2 ${sel ? "text-[#1E5FEB]" : "text-slate-500"}`} />
                        <div className="text-sm font-semibold leading-tight">
                          {devReorder && (
                            <div className="text-[9px] font-mono text-slate-400 tabular-nums select-none mb-1">#{idx + 1}</div>
                          )}
                          {d.label}
                        </div>
                      </OptionCard>
                      {!devReorder && d._custom && d.entryId && (
                        <CatalogQuickActions
                          testidPrefix={`wizard-device-type-${d.entryId}`}
                          canDelete={isAdmin}
                          onEdit={() => openCatalogEditor({ ...d, type: "device_type" })}
                        />
                      )}
                    </div>
                  );
                })}
                {!addDevOpen && !devReorder && (
                  <button
                    type="button"
                    onClick={() => setAddDevOpen(true)}
                    data-testid="wizard-add-dev"
                    className="min-h-[104px] flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border border-dashed border-slate-300 hover:border-[#1E5FEB] hover:bg-slate-50 text-slate-500 hover:text-[#1E5FEB] transition-colors"
                  >
                    <Plus size={18} weight="bold" />
                    <span className="text-[11px] font-bold tracking-wider">ADD NEW</span>
                  </button>
                )}
              </div>
              {addDevOpen && (
                <InlineAdd
                  label="New device type"
                  placeholder="e.g. Gaming Console"
                  value={addDevLabel}
                  onChange={setAddDevLabel}
                  onSave={addDev}
                  onCancel={() => { setAddDevOpen(false); setAddDevLabel(""); }}
                  testid="wizard-add-dev-form"
                />
              )}
            </Step>
          )}

          {step === 2 && (
            <Step title="Which model?" subtitle={`${makeLabel} · ${deviceType?.label}`}>
              <div className="flex gap-2 mb-1.5">
                <div className="relative flex-1">
                  <MagnifyingGlass size={18} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus={!reorderMode}
                    value={modelQuery}
                    onChange={(e) => setModelQuery(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter picks the first filtered match for a fast keyboard flow;
                      // falls back to saving the typed text as a new model if none.
                      if (e.key === "Enter" && !reorderMode) {
                        e.preventDefault();
                        const first = filteredModels[0];
                        if (first) {
                          setModel(first.name);
                          setModelId(first.id || "");
                          setModelMeta({ year: first.year || "", model_number: first.model_number || "" });
                          setModelQuery("");
                        }
                        else if (modelQuery.trim()) {
                          addModel(modelQuery.trim());
                        }
                      }
                      if (e.key === "Escape") { setModelQuery(""); }
                    }}
                    placeholder={model ? `Selected: ${model} — type to search or pick below` : "Search model or number"}
                    className={inputCls + " h-12 text-base flex-1 pl-11 shadow-sm"}
                    data-testid="wizard-model-input"
                    disabled={reorderMode}
                  />
                </div>
                {modelQuery.trim() && !modelsList.some((m) => m.name.toLowerCase() === modelQuery.trim().toLowerCase()) && !reorderMode && (
                  <button
                    type="button"
                    onClick={() => addModel(modelQuery.trim())}
                    data-testid="wizard-save-model"
                    className="h-12 px-5 rounded-2xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-bold tracking-wide flex items-center gap-1.5 whitespace-nowrap transition-colors shadow-sm"
                  >
                    <Plus size={13} weight="bold" /> Add model
                  </button>
                )}
              </div>
              {modelQuery.trim() && !modelsList.some((m) => m.name.toLowerCase() === modelQuery.trim().toLowerCase()) && !reorderMode && filteredModels.length === 0 && (
                <div className="text-[11px] text-[#1E5FEB] mb-3 font-medium">
                  "{modelQuery.trim()}" isn't in your catalog yet — press Enter or click ADD MODEL to save it for next time.
                </div>
              )}
              {(!modelQuery.trim() || filteredModels.length > 0) && (
                <div className="mb-4" />
              )}
              {modelsList.length > 0 ? (
                <div>
                  {/* Toolbar — reorder toggle sits top-right */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] text-slate-500 font-mono">
                      {reorderMode
                        ? <span className="text-[#1E5FEB] font-semibold">Drag to reorder · release to snap into place</span>
                        : modelQuery.trim()
                          ? `${filteredModels.length} of ${modelsList.length} models`
                          : `${modelsList.length} models`}
                    </div>
                    {reorderMode ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.put("/catalog/website/order", {
                              scope: `models:${manufacturer.id}:${deviceType.id}`,
                              order: modelsList.map((m) => m.id).filter(Boolean),
                            });
                            toast.success("Order saved");
                          } catch (e) { toast.error(formatApiError(e) || "Couldn't save order"); }
                          setReorderMode(false);
                        }}
                        data-testid="model-reorder-done"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11px] font-bold tracking-wider"
                      >
                        <Check size={12} weight="bold" /> DONE
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setReorderMode(true)}
                        data-testid="wizard-reorder-toggle"
                        title="Reorder models"
                        className="inline-flex items-center gap-1 h-8 px-2 rounded-full border border-slate-200 hover:border-[#121216] hover:text-[#121216] text-slate-500 text-[11px] font-semibold"
                      >
                        <DotsThreeVertical size={16} weight="bold" />
                      </button>
                    )}
                  </div>

                  <div
                    className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 ${reorderMode ? "rh-wiggle" : ""}`}
                    data-testid="wizard-model-grid"
                  >
                    {filteredModels.map((m, idx) => {
                      const mName = m.name;
                      const meta = [m.year, m.model_number].filter(Boolean).join(" · ");
                      const isSel = modelId ? modelId === m.id : model === mName;
                      return (
                      <div key={m.id || `${mName}-${idx}`} className="relative">
                      <button
                        type="button"
                        draggable={reorderMode}
                        data-dragging={dragIndex === idx ? "true" : "false"}
                        onClick={() => {
                          if (reorderMode) return;
                          setModel(mName);
                          setModelId(m.id || "");
                          setModelMeta({ year: m.year || "", model_number: m.model_number || "" });
                          setModelQuery("");
                        }}
                        onDragStart={() => setDragIndex(idx)}
                        onDragOver={(e) => {
                          if (!reorderMode) return;
                          e.preventDefault();
                          if (dragIndex === null || dragIndex === idx) return;
                          // Rearrange the model-id array in websiteOrders
                          // so the memo re-derives modelsList in the new
                          // order without a round-trip to the backend yet.
                          const scope = `models:${manufacturer?.id}:${deviceType?.id}`;
                          const currentIds = modelsList.map((x) => x.id).filter(Boolean);
                          const [moved] = currentIds.splice(dragIndex, 1);
                          currentIds.splice(idx, 0, moved);
                          setWebsiteOrders((prev) => ({ ...prev, [scope]: currentIds }));
                          setDragIndex(idx);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                        className={`relative w-full min-h-[64px] text-left text-sm p-3.5 ${m._custom ? "pr-16" : "pr-8"} rounded-[18px] border transition-[background-color,border-color,box-shadow,transform] active:scale-[0.98] ${
                          reorderMode
                            ? "border-slate-300 bg-white text-slate-700 cursor-grab active:cursor-grabbing"
                            : isSel
                              ? "bg-gradient-to-br from-blue-50 to-white border-[#1E5FEB] text-slate-950 ring-2 ring-blue-100 shadow-md shadow-blue-100/70 font-semibold"
                              : "bg-white border-slate-200 hover:border-[#1E5FEB]/40 hover:bg-blue-50/40 hover:shadow-md hover:-translate-y-0.5 text-slate-700"
                        }`}
                        data-testid={`model-${(m.id || mName).replace(/\s/g, "_")}`}
                      >
                        {reorderMode && (
                          <span className="absolute top-1 right-1.5 text-[9px] font-mono text-slate-400 tabular-nums select-none">
                            #{idx + 1}
                          </span>
                        )}
                        <div className="font-semibold leading-tight line-clamp-2">{mName}</div>
                        {meta && (
                          <div
                            className={`text-[10.5px] font-mono tabular-nums tracking-wide ${
                              isSel ? "text-[#1E5FEB]/70" : "text-slate-400"
                            }`}
                            title={meta}
                          >
                            {meta}
                          </div>
                        )}
                        {!reorderMode && isSel && (
                          <span className={`absolute top-1/2 -translate-y-1/2 ${m._custom ? "right-12" : "right-2"} w-5 h-5 rounded-full bg-[#1E5FEB] text-white flex items-center justify-center shadow-sm`}>
                            <Check size={11} weight="bold" />
                          </span>
                        )}
                      </button>
                      {!reorderMode && m._custom && m.entryId && (
                        <CatalogQuickActions
                          testidPrefix={`wizard-model-${m.entryId}`}
                          canDelete={isAdmin}
                          onEdit={() => openCatalogEditor({ ...m, label: m.name, type: "model" })}
                        />
                      )}
                      </div>
                      );
                    })}
                    {/* Persistent "add new" tile — always visible so staff know they
                        can add a model that isn't preset. Focuses the input rather
                        than opening a separate modal so the flow is single-tap. */}
                    {!reorderMode && (
                      <button
                        type="button"
                        onClick={() => {
                          const inp = document.querySelector('[data-testid="wizard-model-input"]');
                          if (inp) { inp.focus(); inp.scrollIntoView({ block: "nearest" }); }
                        }}
                        data-testid="wizard-add-new-model-tile"
                        className="min-h-[64px] text-left text-sm p-3.5 rounded-[18px] border border-dashed border-slate-300 bg-white hover:border-[#1E5FEB]/40 hover:bg-blue-50/40 text-[#1E5FEB] font-semibold flex items-center gap-1.5 transition-colors"
                      >
                        <Plus size={13} weight="bold" /> Add new model
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-2xl p-8 text-center">
                  No presets for this combination. Type the model name above, then hit <strong>ADD MODEL</strong> to save it to your catalog for next time.
                </div>
              )}
            </Step>
          )}

          {step === 3 && (
            <Step title="What's wrong?" subtitle="Select all that apply. Prices are from your website — override per-line if you need to.">
              {/* Stock availability toggle */}
              <div className="flex items-center gap-2 mb-3" data-testid="wizard-stock-toggle-bar">
                <button
                  type="button"
                  onClick={() => setShowStockAvailability(!showStockAvailability)}
                  data-testid="wizard-stock-toggle"
                  className={`h-8 px-3 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-all border ${
                    showStockAvailability
                      ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                      : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${showStockAvailability ? "bg-emerald-500" : "bg-slate-300"}`} />
                  {showStockAvailability ? "Stock: ON" : "Show stock"}
                </button>
              </div>
              <WiggleToolbar
                count={allProblems.length}
                reorder={probReorder}
                setReorder={setProbReorder}
                onDone={async () => {
                  try {
                    await api.put("/catalog/website/order", {
                      scope: `repairs:${manufacturer?.id}:${deviceType?.id}:${selectedWebsiteModel?.id || ""}`,
                      order: allProblems.filter((p) => p._website).map((p) => p._rawId),
                    });
                    // Mirror as a label order so the catalog manager (which
                    // orders presets and customs too) stays in step.
                    await api.put("/catalog/website/order", {
                      scope: repairOrderScope(manufacturer?.id, deviceType?.id, selectedWebsiteModel?.id),
                      order: allProblems.map((p) => p.label),
                    });
                    toast.success("Order saved");
                  } catch (e) { toast.error(formatApiError(e) || "Couldn't save order"); }
                  setProbReorder(false);
                }}
                testidPrefix="prob"
              />
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 ${probReorder ? "rh-wiggle" : ""}`}
                data-testid="wizard-problem-grid"
              >
                {allProblems.map((p, idx) => {
                  const sel = problems.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      draggable={probReorder}
                      data-dragging={probDragIdx === idx ? "true" : "false"}
                      onDragStart={() => setProbDragIdx(idx)}
                      onDragOver={(e) => {
                        if (!probReorder || probDragIdx === null || probDragIdx === idx) return;
                        e.preventDefault();
                        // Rearrange the repair-id list in websiteOrders
                        // so the memo re-derives allProblems in the new
                        // order. Only reorder among _website rows (skip
                        // ad-hoc customProblems — they append after).
                        const scope = `repairs:${manufacturer?.id}:${deviceType?.id}:${selectedWebsiteModel?.id || ""}`;
                        const webRawIds = allProblems.filter((p) => p._website).map((p) => p._rawId);
                        const dragged = allProblems[probDragIdx];
                        if (!dragged?._website) { setProbDragIdx(null); return; }
                        const target = allProblems[idx];
                        if (!target?._website) { setProbDragIdx(null); return; }
                        const fromWebIdx = webRawIds.indexOf(dragged._rawId);
                        const toWebIdx = webRawIds.indexOf(target._rawId);
                        if (fromWebIdx < 0 || toWebIdx < 0) return;
                        const [moved] = webRawIds.splice(fromWebIdx, 1);
                        webRawIds.splice(toWebIdx, 0, moved);
                        setWebsiteOrders((prev) => ({ ...prev, [scope]: webRawIds }));
                        setProbDragIdx(idx);
                      }}
                      onDragEnd={() => setProbDragIdx(null)}
                      className={`relative rounded-[18px] border transition-all duration-200 overflow-hidden ${
                        probReorder
                          ? "border-slate-300 bg-white cursor-grab active:cursor-grabbing"
                          : sel
                            ? "bg-gradient-to-br from-blue-50 to-white border-[#1E5FEB] ring-2 ring-blue-100 shadow-md shadow-blue-100/70"
                            : "border-slate-200 bg-white hover:border-[#1E5FEB]/40 hover:bg-blue-50/40 hover:shadow-md hover:-translate-y-0.5"
                      }`}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (probReorder) return;
                          setProblems((prev) => {
                            if (sel) {
                              setEditingProblemPriceId("");
                              return prev.filter((x) => x !== p.id);
                            }
                            setEditingProblemPriceId(p.id);
                            return [...prev, p.id];
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (probReorder) return;
                            setProblems((prev) => {
                              if (sel) {
                                setEditingProblemPriceId("");
                                return prev.filter((x) => x !== p.id);
                              }
                              setEditingProblemPriceId(p.id);
                              return [...prev, p.id];
                            });
                          }
                        }}
                        className="w-full min-h-[66px] flex items-center gap-3 p-3.5 text-left cursor-pointer"
                        data-testid={`problem-${p.id}`}
                      >
                        {probReorder && (
                          <span className="text-[9px] font-mono text-slate-400 tabular-nums select-none shrink-0 w-6">#{idx + 1}</span>
                        )}
                        {!probReorder && (
                          <span
                            className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
                              sel
                                ? "bg-[#1E5FEB] border-[#1E5FEB]"
                                : "border border-slate-300 bg-white"
                            }`}
                          >
                            {sel && <Check size={12} weight="bold" className="text-white" />}
                          </span>
                        )}
                        <span className="flex-1 min-w-0 flex items-center gap-1.5 leading-tight">
                          <span className="text-sm font-semibold text-slate-800 line-clamp-2 truncate">
                            {p.label}
                          </span>
                          {p._custom && (
                            p.entryId ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openCatalogEditor({ ...p, type: "problem" });
                                }}
                                data-testid={`wizard-problem-${p.entryId}-edit-button`}
                                title="Edit custom repair"
                                className="group/custom inline-flex items-center gap-1 h-5 pl-1.5 pr-1 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-600 hover:text-white text-[9px] font-bold tracking-wide shrink-0 transition-colors cursor-pointer"
                              >
                                <Sparkle size={8} weight="fill" />
                                <span>CUSTOM</span>
                                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/70 group-hover/custom:bg-white text-violet-600 shadow-sm">
                                  <PencilSimple size={7} weight="bold" />
                                </span>
                              </button>
                            ) : (
                              <span
                                className="inline-flex items-center gap-0.5 h-4 px-1.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold tracking-wide shrink-0"
                                title="Custom repair"
                              >
                                <Sparkle size={8} weight="fill" />
                                CUSTOM
                              </span>
                            )
                          )}
                        </span>
                        {/* Variant-count chip — shown next to price so
                            cashiers know at a glance a dropdown is coming. */}
                        {Array.isArray(p._partOptions) && p._partOptions.length > 1 && (
                          <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[9px] font-bold tracking-wide shrink-0 ${
                            sel ? "bg-[#1E5FEB] text-white" : "bg-blue-100 text-blue-700"
                          }`}>
                            {p._partOptions.length} OPTIONS{!sel && " *"}
                          </span>
                        )}
                        {/* Stock availability dot */}
                        {showStockAvailability && (() => {
                          const multiOpt = Array.isArray(p._partOptions) && p._partOptions.length > 1;
                          const optId = multiOpt ? (problemPartOptionIds[p.id] || p._partOptions[0]?.id) : null;
                          const stockInfo = multiOpt
                            ? wizardStockMap[`${p._stockKey}::opt:${optId}`]
                            : (wizardStockMap[p.entryId] || wizardStockMap[p._stockKey] || wizardStockMap[p._rawId]);
                          if (!stockInfo) return <span className="w-2 h-2 rounded-full bg-slate-200 shrink-0" title="Not tracked" />;
                          if (stockInfo.status === "out") return (
                            <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold shrink-0" data-testid={`stock-dot-${p.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> 0
                            </span>
                          );
                          if (stockInfo.status === "low") return (
                            <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-amber-100 text-amber-600 text-[9px] font-bold shrink-0" data-testid={`stock-dot-${p.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {stockInfo.stock}
                            </span>
                          );
                          return (
                            <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-emerald-100 text-emerald-600 text-[9px] font-bold shrink-0" data-testid={`stock-dot-${p.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {stockInfo.stock}
                            </span>
                          );
                        })()}
                        <span
                          data-testid={`problem-price-${p.id}-label`}
                          className={`font-mono text-xs tabular-nums shrink-0 rounded-full px-2.5 py-1 border ${
                            sel
                              ? "bg-white text-slate-950 border-slate-200 shadow-sm"
                              : "bg-slate-50 text-slate-600 border-transparent"
                          }`}
                        >
                          ~${money(priceForProblem(p.id))}
                        </span>
                      </div>
                      {!probReorder && sel && editingProblemPriceId === p.id && (
                        <div
                          className="border-t border-blue-100/70 bg-gradient-to-b from-blue-50/40 to-white px-3.5 py-3 flex items-center gap-3"
                          data-testid={`problem-price-editor-${p.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[10px] font-bold tracking-[0.16em] text-slate-500 shrink-0">PRICE</span>
                          <div className="flex-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 focus-within:border-[#1E5FEB] focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                            <span className="text-sm font-mono text-slate-400">$</span>
                            <input
                              autoFocus
                              type="text"
                              inputMode="decimal"
                              pattern="[0-9]*\.?[0-9]*"
                              value={problemPrices[p.id] ?? priceForProblem(p.id)}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                                  setProblemPrices((pp) => ({ ...pp, [p.id]: v }));
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "Escape") setEditingProblemPriceId("");
                              }}
                              className="flex-1 h-6 border-0 bg-transparent p-0 font-mono text-sm text-slate-900 focus:outline-none focus:ring-0"
                              data-testid={`problem-price-${p.id}`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditingProblemPriceId("")}
                            data-testid={`problem-price-done-${p.id}`}
                            className="h-9 px-3.5 rounded-lg bg-[#1E5FEB] hover:bg-blue-700 text-white text-xs font-bold tracking-wide flex items-center gap-1.5 shadow-sm transition-colors"
                          >
                            <Check size={12} weight="bold" />
                            DONE
                          </button>
                        </div>
                      )}
                      {/* Part-option picker — website part variants (grades
                          like In-cell / Soft OLED / Premium) shown as rich
                          chips with their own price and live stock so the
                          cashier sees everything at a glance.
                          Only visible when the repair is SELECTED (toggle). */}
                      {!probReorder && sel && Array.isArray(p._partOptions) && p._partOptions.length > 1 && (
                        <div
                          className="px-3.5 pb-3.5 pt-3 border-t border-blue-100 bg-gradient-to-b from-blue-50/50 to-white animate-in slide-in-from-top-1 fade-in duration-200"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`problem-part-options-${p.id}`}
                        >
                          <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-1.5 px-0.5" data-testid={`problem-part-options-heading-${p.id}`}>Select grade</div>
                          <div className="grid grid-cols-1 gap-1.5">
                            {p._partOptions.map((o) => {
                              const chosen = (problemPartOptionIds[p.id] || p._partOptions[0]?.id) === o.id;
                              const si = wizardStockMap[`${p._stockKey}::opt:${o.id}`];
                              const stock = si ? si.stock : null;
                              const stat = si?.status || "untracked";
                              const dot = !si ? "bg-slate-300"
                                : stat === "out" ? "bg-red-500"
                                : stat === "low" ? "bg-amber-500" : "bg-emerald-500";
                              const stockLabel = !si ? { cls: "text-slate-400", text: "Not tracked" }
                                : stat === "out" ? { cls: "text-red-600", text: "Out of stock" }
                                : stat === "low" ? { cls: "text-amber-600", text: `${stock} left` }
                                : { cls: "text-emerald-600", text: `${stock} in stock` };
                              return (
                                <button
                                  key={o.id}
                                  type="button"
                                  onClick={() => {
                                    setProblemPartOptionIds((prev) => ({ ...prev, [p.id]: o.id }));
                                    setProblemPrices((pp) => { const n = { ...pp }; delete n[p.id]; return n; });
                                  }}
                                  data-testid={`problem-part-opt-${p.id}-${o.id}`}
                                  className={`group w-full flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all duration-150 ${
                                    chosen
                                      ? "border-[#1E5FEB] bg-[#1E5FEB]/[0.045] ring-1 ring-[#1E5FEB]/25 shadow-sm"
                                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/70"
                                  }`}
                                  title={si ? `${stock} in stock` : "Not tracked in inventory"}
                                >
                                  <span className={`shrink-0 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-colors ${chosen ? "border-[#1E5FEB]" : "border-slate-300 group-hover:border-slate-400"}`} aria-hidden="true">
                                    {chosen && <span className="w-2 h-2 rounded-full bg-[#1E5FEB]" />}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className={`block text-[12.5px] font-semibold truncate ${chosen ? "text-[#1E3A8A]" : "text-slate-700"}`}>{o.label}</span>
                                    <span className="block text-[11px] text-slate-400 font-medium">${Number(o.price || 0).toFixed(2)}</span>
                                  </span>
                                  <span className={`shrink-0 inline-flex items-center gap-1.5 text-[10.5px] font-bold ${stockLabel.cls}`} data-testid={`problem-part-opt-stock-${p.id}-${o.id}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                    {stockLabel.text}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>

              {/* Add custom issue */}
              {!customFormOpen ? (
                <button
                  type="button"
                  onClick={() => setCustomFormOpen(true)}
                  data-testid="add-custom-issue"
                  className="w-full h-11 mb-5 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#1E5FEB] hover:bg-blue-50/30 text-slate-600 hover:text-[#1E5FEB] text-[13px] font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus size={14} weight="bold" /> ADD NEW ISSUE
                </button>
              ) : (
                <div className="mb-5 rounded-xl border border-[#1E5FEB] bg-blue-50/30 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E5FEB] font-semibold mb-2">
                    NEW ISSUE
                  </div>
                  <div className="flex gap-2">
                    <input
                      autoFocus
                      value={customDraft.label}
                      onChange={(e) => setCustomDraft({ ...customDraft, label: e.target.value })}
                      placeholder="e.g. Speaker grille cleaning"
                      className={inputCls + " flex-1"}
                      data-testid="custom-issue-label"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          document.querySelector('[data-testid="custom-issue-save"]')?.click();
                        }
                      }}
                    />
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono">$</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={customDraft.price}
                        onChange={(e) => setCustomDraft({ ...customDraft, price: e.target.value })}
                        placeholder="0.00"
                        className={inputCls + " pl-6 font-mono text-right"}
                        data-testid="custom-issue-price"
                      />
                    </div>
                    <button
                      type="button"
                      data-testid="custom-issue-save"
                      onClick={async () => {
                        const label = customDraft.label.trim();
                        if (!label) return toast.error("Enter an issue name");
                        const price = Number(customDraft.price || 0);
                        const scope = customDraft.scope;
                        const scopedModelId = modelId || "";
                        const saved = await saveCatalog({
                          type: "problem",
                          label,
                          price,
                          parent_manufacturer: scope === "all" ? "" : (manufacturer?.id || ""),
                          parent_device_type:
                            scope === "all" ? "all" : (deviceType?.id || "all"),
                          parent_model: scope === "model" ? scopedModelId : "",
                        });
                        if (saved) {
                          setProblems((prev) => [...prev, `cat_${saved.id}`]);
                        }
                        setCustomDraft({ label: "", price: "", scope: "model" });
                        setCustomFormOpen(false);
                      }}
                      className="h-10 px-4 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-semibold shrink-0"
                    >
                      ADD
                    </button>
                    <button
                      type="button"
                      onClick={() => { setCustomFormOpen(false); setCustomDraft({ label: "", price: "", scope: "model" }); }}
                      className="h-10 px-3 rounded-xl border border-slate-200 hover:border-slate-400 text-[12px] font-semibold text-slate-600 shrink-0"
                    >
                      CANCEL
                    </button>
                  </div>

                  {/* Visibility scope — dedicated row, 3 options */}
                  <div className="mt-3 pt-3 border-t border-blue-100/80 flex items-center gap-3 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                      Show for
                    </div>
                    <div
                      className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-white border border-slate-200"
                      data-testid="custom-issue-scope-bar"
                    >
                      {[
                        {
                          id: "model",
                          label: model ? `Only ${model}` : "This model only",
                          testid: "custom-issue-scope-model",
                        },
                        {
                          id: "device",
                          label: deviceType?.label ? `All ${makeLabel} ${deviceType.label}` : "This device family",
                          testid: "custom-issue-scope-device",
                        },
                        {
                          id: "all",
                          label: "All devices",
                          testid: "custom-issue-scope-all",
                        },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setCustomDraft({ ...customDraft, scope: opt.id })}
                          data-testid={opt.testid}
                          className={`h-7 px-3 rounded-full text-[11px] font-semibold transition-all ${
                            customDraft.scope === opt.id
                              ? "bg-[#1E5FEB] text-white"
                              : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {customDraft.scope === "model" && (
                        manufacturer?.label && deviceType?.label
                          ? `Appears only for this selected model.`
                          : "Appears only for this model."
                      )}
                      {customDraft.scope === "device" && (
                        deviceType?.label
                          ? `Appears for all ${makeLabel} ${deviceType.label} models.`
                          : "Appears for this brand + device."
                      )}
                      {customDraft.scope === "all" && "Appears for every device."}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label>Intake note <span className="text-[10px] text-slate-400 font-normal">(prints on the customer&apos;s receipt)</span></Label>
                <textarea
                  value={problemNotes}
                  onChange={(e) => setProblemNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. Backup done, no case included, prefers pickup after 4pm…"
                  className={inputCls + " py-2 h-auto"}
                  data-testid="wizard-notes"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Saved as the first note on the ticket (with your name + time stamp) and <strong>printed on the customer&apos;s intake receipt</strong> plus emailed PDF. Any technician-only notes should be added on the ticket detail page after intake — those stay internal.
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 border border-slate-800 px-6 py-4 shadow-lg shadow-slate-200/50">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300 font-semibold">Estimated total</div>
                <div className="font-mono font-black text-3xl bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent tabular-nums tracking-tight">${money(estimated)}</div>
              </div>
            </Step>
          )}

          {step === 4 && (
            <Step title="Customer & device details" subtitle="Select the customer, then capture the device identifiers.">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Customer card */}
                <div className="rounded-[20px] border border-slate-200 bg-white overflow-hidden shadow-sm shadow-slate-200/50" data-testid="wizard-customer-panel">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                        <User size={15} weight="bold" className="text-[#1E5FEB]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Customer</div>
                        <div className="text-[12px] text-slate-400 truncate" data-testid="wizard-customer-panel-status">
                          {showNewCust ? "Enter new customer details" : customerId ? "Customer selected" : "Search or add a customer"}
                        </div>
                      </div>
                    </div>
                    {!showNewCust && (
                      <button
                        onClick={() => setShowNewCust(true)}
                        className="h-9 px-3 rounded-xl border border-slate-200 bg-white hover:border-[#1E5FEB] text-[11px] font-semibold text-[#1E5FEB] flex items-center gap-1 transition-colors"
                        data-testid="wizard-new-cust-open"
                      >
                        <Plus size={12} weight="bold" /> New
                      </button>
                    )}
                  </div>
                  <div className="p-4 bg-[#FAFBFC]">
                    {!showNewCust ? (
                      <>
                        <div className="relative mb-3">
                          <MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            placeholder="Search by name or phone"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            className={inputCls + " pl-10 bg-white shadow-sm"}
                            data-testid="wizard-cust-search"
                            autoFocus
                          />
                          {customerSearch && (
                            <button
                              type="button"
                              onClick={() => setCustomerSearch("")}
                              data-testid="wizard-cust-search-clear"
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
                              title="Clear"
                            >
                              <X size={11} weight="bold" />
                            </button>
                          )}
                        </div>
                        {/*
                          Design: when empty, show a friendly prompt with the
                          two actions staff always take at this step — search
                          or create. When a search is typed, render a list of
                          premium cards so staff can see at a glance who's who
                          without opening the customer profile.
                        */}
                        {customerSearch.trim().length < 2 && !customerId ? (
                          <div
                            className="rounded-2xl border border-dashed border-slate-200 bg-white py-8 px-4 text-center space-y-3 shadow-sm shadow-slate-100/70"
                            data-testid="wizard-cust-empty"
                          >
                            <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
                              <MagnifyingGlass size={18} weight="bold" className="text-[#1E5FEB]" />
                            </div>
                            <div>
                              <div className="text-[13px] font-semibold text-slate-700">
                                Search existing customers
                              </div>
                              <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                                Type at least 2 letters of the name, phone or email.
                                <br />Or <button type="button" onClick={() => setShowNewCust(true)} data-testid="wizard-cust-empty-add-button" className="text-[#1E5FEB] font-semibold hover:underline">add a new customer</button>.
                              </div>
                            </div>
                          </div>
                        ) : (
                          (() => {
                            // Once a customer is locked in, collapse the list
                            // down to just that one card — no more scrolling
                            // through same-name duplicates. Staff can clear
                            // the selection via the "Change" action below to
                            // reopen the full search again.
                            const selectedCust = customerId
                              ? customers.find((c) => c.id === customerId)
                              : null;
                            const visible = selectedCust ? [selectedCust] : customers;
                            return (
                              <div className="space-y-2 max-h-[360px] overflow-auto -mx-1 px-1" data-testid="wizard-customer-results">
                                {visible.length === 0 ? (
                                  <div
                                    className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-5 text-center"
                                    data-testid="wizard-cust-no-match"
                                  >
                                    <div className="text-[13px] font-semibold text-slate-700">
                                      No customers match "{customerSearch}"
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1">
                                      <button type="button" onClick={() => setShowNewCust(true)} data-testid="wizard-cust-no-match-create" className="text-[#1E5FEB] font-semibold hover:underline">
                                        Create "{customerSearch}" as a new customer →
                                      </button>
                                    </div>
                                  </div>
                                ) : visible.map((c) => (
                                  editCustId === c.id ? (
                                    <div
                                      key={c.id}
                                      className="rounded-2xl border-2 border-[#1E5FEB] bg-blue-50/50 p-4 space-y-2.5"
                                      data-testid={`wizard-cust-edit-${c.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-[#1E5FEB]">Editing customer</div>
                                        <button
                                          type="button"
                                          onClick={cancelEditCustomer}
                                          data-testid={`wizard-cust-edit-cancel-${c.id}`}
                                          className="text-[11px] text-slate-500 hover:text-slate-900"
                                        >Cancel</button>
                                      </div>
                                      <input
                                        value={editCust.name}
                                        onChange={(e) => setEditCust({ ...editCust, name: e.target.value })}
                                        placeholder="Name"
                                        className={inputCls}
                                        data-testid={`wizard-cust-edit-name-${c.id}`}
                                      />
                                      <input
                                        value={editCust.phone}
                                        onChange={(e) => setEditCust({ ...editCust, phone: e.target.value })}
                                        placeholder="Phone"
                                        className={inputCls + " font-mono"}
                                        data-testid={`wizard-cust-edit-phone-${c.id}`}
                                      />
                                      <input
                                        value={editCust.email}
                                        onChange={(e) => setEditCust({ ...editCust, email: e.target.value })}
                                        placeholder="Email (optional)"
                                        className={inputCls}
                                        data-testid={`wizard-cust-edit-email-${c.id}`}
                                      />
                                      <div className="flex gap-2 pt-1">
                                        <button
                                          type="button"
                                          onClick={saveEditCustomer}
                                          disabled={savingEdit}
                                          data-testid={`wizard-cust-edit-save-${c.id}`}
                                          className="flex-1 h-10 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-300 text-white text-[12px] font-semibold tracking-wider uppercase"
                                        >
                                          {savingEdit ? "Saving…" : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteCustomerFromPicker(c)}
                                          data-testid={`wizard-cust-edit-delete-${c.id}`}
                                          className="h-10 px-3 rounded-xl border border-red-200 text-red-600 hover:border-red-500 hover:bg-red-50 text-[12px] font-semibold tracking-wider uppercase flex items-center gap-1.5"
                                          title="Delete customer"
                                        >
                                          <Trash size={12} weight="bold" /> Delete
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <CustomerCard
                                      key={c.id}
                                      c={c}
                                      selected={customerId === c.id}
                                      onSelect={() => setCustomerId(c.id)}
                                      onEdit={() => startEditCustomer(c)}
                                      openBookings={openBookingsByCustomer[c.id] || []}
                                    />
                                  )
                                ))}
                                {selectedCust && (
                                  <>
                                    {/* Unconverted-booking banner — when this customer
                                        still has bookings with status 'new' or 'confirmed'.
                                        Clicking "Convert" jumps to /bookings filtered to
                                        their open bookings so the existing convert-to-ticket
                                        flow can pick which one to use. */}
                                    {(openBookingsByCustomer[selectedCust.id] || []).length > 0 && (
                                      <div
                                        className="rounded-xl border-2 border-[#FCD34D] bg-[#FFFBEB] p-3 flex items-start gap-2.5"
                                        data-testid="wizard-cust-open-bookings-flag"
                                      >
                                        <div className="shrink-0 w-7 h-7 rounded-full bg-[#FCD34D] flex items-center justify-center mt-0.5">
                                          <span className="text-[#78350F] text-[14px] leading-none font-black">!</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-[12px] font-bold uppercase tracking-wider text-[#92400E]">
                                            {(openBookingsByCustomer[selectedCust.id] || []).length} unconverted booking{(openBookingsByCustomer[selectedCust.id] || []).length === 1 ? "" : "s"}
                                          </div>
                                          <div className="text-[11.5px] text-[#78350F] mt-0.5 truncate">
                                            Latest: {(openBookingsByCustomer[selectedCust.id] || [])[0]?.device_make || ""} {(openBookingsByCustomer[selectedCust.id] || [])[0]?.device_model || ""}
                                            {(openBookingsByCustomer[selectedCust.id] || [])[0]?.device_year ? ` (${(openBookingsByCustomer[selectedCust.id] || [])[0].device_year})` : ""}
                                            {(openBookingsByCustomer[selectedCust.id] || [])[0]?.device_model_number ? ` · ${(openBookingsByCustomer[selectedCust.id] || [])[0].device_model_number}` : ""}
                                            {(openBookingsByCustomer[selectedCust.id] || [])[0]?.problem ? ` · ${(openBookingsByCustomer[selectedCust.id] || [])[0].problem}` : ""}
                                          </div>
                                          {(openBookingsByCustomer[selectedCust.id] || []).length === 1 ? (
                                            // ONE booking: hot-prefill the
                                            // wizard from the booking data
                                            // so the cashier doesn't have
                                            // to retype anything. We don't
                                            // call the convert endpoint here
                                            // because that creates a ticket
                                            // immediately — instead we let
                                            // the cashier review/edit the
                                            // intake, then call convert when
                                            // they hit Save (handled by the
                                            // wizard's onComplete logic).
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const b = (openBookingsByCustomer[selectedCust.id] || [])[0];
                                                if (!b) return;
                                                // Skip the wizard entirely —
                                                // hit the convert endpoint
                                                // directly. The cashier can
                                                // still edit the resulting
                                                // ticket from RepairDetail.
                                                api.post(`/bookings/${b.id}/convert`, {})
                                                  .then((r) => {
                                                    toast.success(`Booking converted → ${r.data.ticket_number}`);
                                                    if (r.data?.ticket_id) {
                                                      window.location.href = `/repairs/${r.data.ticket_id}`;
                                                    }
                                                  })
                                                  .catch((e) => toast.error(formatApiError(e)));
                                              }}
                                              className="inline-block mt-1.5 h-7 px-3 rounded-full bg-[#92400E] text-white text-[11px] font-semibold hover:bg-[#78350F] transition-colors leading-7"
                                              data-testid="wizard-convert-booking-btn"
                                            >
                                              Convert this booking →
                                            </button>
                                          ) : (
                                            <a
                                              href={`/bookings?customer_id=${selectedCust.id}&status=new`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-block mt-1.5 h-7 px-3 rounded-full bg-[#92400E] text-white text-[11px] font-semibold hover:bg-[#78350F] transition-colors leading-7"
                                              data-testid="wizard-convert-booking-btn"
                                            >
                                              Pick from {(openBookingsByCustomer[selectedCust.id] || []).length} bookings →
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Reset to search mode so staff can
                                        // pick a different customer without
                                        // re-typing. Keep the current query
                                        // so the list re-populates instantly.
                                        setCustomerId("");
                                      }}
                                      data-testid="wizard-cust-change"
                                      className="w-full h-9 rounded-xl border border-dashed border-slate-200 hover:border-slate-400 text-[11px] font-semibold tracking-wider uppercase text-slate-500 hover:text-slate-800 transition-colors"
                                    >
                                      Change customer
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </>
                    ) : (
                      <div className="space-y-3" data-testid="wizard-new-customer-form">
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/50 px-3 py-2.5 flex items-center gap-2" data-testid="wizard-new-customer-heading">
                          <div className="w-8 h-8 rounded-xl bg-white border border-blue-100 flex items-center justify-center">
                            <User size={13} weight="bold" className="text-[#1E5FEB]" />
                          </div>
                          <div>
                            <div className="text-[12px] font-bold text-slate-900">New customer</div>
                            <div className="text-[11px] text-slate-500">Add contact details for this intake.</div>
                          </div>
                        </div>
                        <FieldRow label="Name">
                          <input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className={inputCls} data-testid="wizard-new-cust-name" />
                        </FieldRow>
                        <FieldRow label="Phone">
                          <input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} className={inputCls + " font-mono"} data-testid="wizard-new-cust-phone" />
                        </FieldRow>

                        {phoneMatches.length > 0 && (
                          <div
                            className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5"
                            data-testid="wizard-phone-dup-warning"
                          >
                            <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] font-semibold text-amber-900">
                                Customer already exists
                              </div>
                              <div className="text-[11px] text-amber-800 mt-0.5">
                                A customer with this phone number is already in your address book.
                              </div>
                              <div className="mt-2 space-y-1">
                                {phoneMatches.map((m) => (
                                  <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => applyExistingCustomer(m)}
                                    className="w-full flex items-center justify-between bg-white hover:bg-amber-100/60 border border-amber-200 rounded-lg px-2.5 py-1.5 text-left transition-colors"
                                    data-testid={`wizard-use-existing-${m.id}`}
                                  >
                                    <div className="min-w-0">
                                      <div className="text-[13px] font-medium text-slate-800 truncate">{m.name}</div>
                                      <div className="text-[11px] font-mono text-slate-500">{m.phone}</div>
                                    </div>
                                    <span className="text-[10px] font-bold tracking-wider text-amber-700 shrink-0 ml-2">
                                      USE THIS →
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {checkingPhone && phoneMatches.length === 0 && normalizePhone(newCust.phone).length >= 4 && (
                          <div className="text-[11px] text-slate-400 font-mono">checking duplicates…</div>
                        )}

                        <FieldRow label="Email">
                          <input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} className={inputCls} data-testid="wizard-new-cust-email" />
                        </FieldRow>
                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            onClick={addCustomer}
                            disabled={phoneMatches.length > 0}
                            className="flex-1 h-10 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-semibold transition-colors"
                            data-testid="wizard-new-cust-save"
                          >
                            Save customer
                          </button>
                          <button type="button" onClick={() => { setShowNewCust(false); setPhoneMatches([]); }} data-testid="wizard-new-cust-cancel" className="h-10 px-4 rounded-xl border border-slate-200 bg-white hover:border-slate-400 text-xs font-semibold text-slate-700">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-slate-100 p-4 space-y-2.5 bg-white" data-testid="wizard-device-identifiers-panel">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                        <DeviceMobile size={13} weight="bold" className="text-slate-500" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Device identifiers</div>
                        <div className="text-[10px] text-slate-400">Optional, but useful for intake labels and lookup.</div>
                      </div>
                    </div>
                    <FieldRow label="IMEI / Serial">
                      <input value={imei} onChange={(e) => setImei(e.target.value)} className={inputCls + " font-mono"} data-testid="wizard-imei" />
                    </FieldRow>
                    <FieldRow label="Passcode / Pattern">
                      <input value={passcode} onChange={(e) => setPasscode(e.target.value)} className={inputCls + " font-mono"} data-testid="wizard-passcode" />
                    </FieldRow>
                  </div>
                </div>

                {/* Summary */}
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600 font-semibold flex items-center gap-2">
                      <Wrench size={13} weight="bold" /> TICKET SUMMARY
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <SummaryRow label="Brand" value={makeLabel} />
                    <SummaryRow label="Device" value={deviceType?.label} />
                    <SummaryRow label="Model" value={model} mono />
                    <div className="border-t border-slate-100 pt-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">ISSUES</div>
                      {problems.length === 0 && <div className="text-xs text-slate-400">None selected.</div>}
                      {problems.map((id) => {
                        const p = allProblems.find((x) => x.id === id);
                        const price = priceForProblem(id);
                        return (
                          <div key={id} className="flex items-center justify-between text-sm mb-1 text-slate-800">
                            <span>{p?.label}</span>
                            <span className="font-mono font-semibold">${money(price)}</span>
                          </div>
                        );
                      })}
                      {problemNotes.trim() && (
                        <div className="mt-2 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2">
                          {problemNotes}
                        </div>
                      )}
                    </div>
                    <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
                      <div className="font-semibold text-slate-700 text-sm">ESTIMATED TOTAL</div>
                      <div className="font-mono font-black text-2xl text-slate-900" data-testid="wizard-estimated">
                        ${money(estimated)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Step>
          )}

          {step === 5 && (
            <Step title="Ready to go" subtitle={batch.length > 0
              ? `${batch.length + (hasCurrentDraft ? 1 : 0)} devices in this intake. Create all as one invoice.`
              : "Create the ticket now. Optionally collect payment upfront."}>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-4" data-testid="wizard-checkout-step">
                {/* Ticket preview */}
                <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60" data-testid="checkout-preview-card">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                        <Receipt size={15} weight="bold" className="text-[#1E5FEB]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                          {batch.length > 0 ? "Multi-device checkout" : "Checkout summary"}
                        </div>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600" data-testid="checkout-preview-device-count">
                      {batch.length + (hasCurrentDraft ? 1 : 0)} device{batch.length + (hasCurrentDraft ? 1 : 0) === 1 ? "" : "s"}
                    </span>
                  </div>

                  {selectedCustomer && (
                    <div
                      className="mb-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-100/70"
                      data-testid="checkout-customer-details-card"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
                          <User size={16} weight="bold" className="text-[#1E5FEB]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Customer details</div>
                          <div className="mt-1 text-base font-bold text-slate-950 truncate" data-testid="checkout-customer-name">
                            {selectedCustomer.name || "—"}
                          </div>
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            {selectedCustomer.phone && (
                              <div className="flex items-center gap-2 min-w-0 rounded-xl bg-white border border-slate-100 px-3 py-2" data-testid="checkout-customer-phone">
                                <Phone size={13} weight="bold" className="text-slate-400 shrink-0" />
                                <span className="font-mono text-slate-700 truncate">{selectedCustomer.phone}</span>
                              </div>
                            )}
                            {selectedCustomer.email && (
                              <div className="flex items-center gap-2 min-w-0 rounded-xl bg-white border border-slate-100 px-3 py-2" data-testid="checkout-customer-email">
                                <EnvelopeSimple size={13} weight="bold" className="text-slate-400 shrink-0" />
                                <span className="text-slate-700 truncate">{selectedCustomer.email}</span>
                              </div>
                            )}
                            {selectedCustomer.address && (
                              <div className="flex items-center gap-2 min-w-0 rounded-xl bg-white border border-slate-100 px-3 py-2 sm:col-span-2" data-testid="checkout-customer-address">
                                <MapPin size={13} weight="bold" className="text-slate-400 shrink-0" />
                                <span className="text-slate-700 truncate">{selectedCustomer.address}</span>
                              </div>
                            )}
                            {selectedCustomer.notes && (
                              <div className="flex items-start gap-2 min-w-0 rounded-xl bg-white border border-slate-100 px-3 py-2 sm:col-span-2" data-testid="checkout-customer-notes">
                                <NotePencil size={13} weight="bold" className="text-slate-400 shrink-0 mt-0.5" />
                                <span className="text-slate-700 line-clamp-2">{selectedCustomer.notes}</span>
                              </div>
                            )}
                            {!selectedCustomer.phone && !selectedCustomer.email && !selectedCustomer.address && !selectedCustomer.notes && (
                              <div className="rounded-xl bg-white border border-slate-100 px-3 py-2 text-slate-500 sm:col-span-2" data-testid="checkout-customer-no-extra-info">
                                No phone, email, address or notes saved.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Parked devices from previous add-another-device cycles */}
                  {batch.length > 0 && (
                    <div className="mb-3 space-y-1.5 rounded-xl border border-blue-100 bg-blue-50/40 p-2.5">
                      {batch.map((d) => (
                        <div key={d.uid} className="flex items-center justify-between text-sm">
                          <div className="min-w-0 truncate">
                            <span className="font-semibold text-slate-800">{d.device_make} {d.device_model}</span>
                            <span className="text-slate-500 ml-1.5 text-[11px] truncate">· {(d.issue || "").split("\n")[0]?.replace(/^•\s*/, "")}</span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 ml-2">
                            <span className="font-mono font-semibold text-slate-800">${money(d.estimated_cost || 0)}</span>
                            <button
                              type="button"
                              onClick={() => removeBatchDevice(d.uid)}
                              className="w-5 h-5 rounded text-slate-400 hover:text-rose-600 hover:bg-white"
                              data-testid={`wizard-preview-remove-${d.uid}`}
                              title="Remove device"
                            ><X size={10} weight="bold" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Current device being finalised */}
                  {hasCurrentDraft && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-100/70" data-testid="checkout-current-device-card">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Selected device & repairs</div>
                          <div className="mt-1 font-heading font-bold text-xl text-slate-900 leading-tight truncate" data-testid="checkout-selected-device-name">
                            {makeLabel} {model}
                          </div>
                        </div>
                        <span className="shrink-0 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600" data-testid="checkout-selected-problem-count">
                          {problems.length} repair{problems.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      {(imei || passcode) && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs" data-testid="checkout-selected-device-identifiers">
                          {imei && (
                            <div className="rounded-xl bg-white border border-slate-100 px-3 py-2 min-w-0">
                              <span className="text-slate-400 uppercase tracking-[0.12em] font-bold">IMEI / Serial</span>
                              <div className="font-mono text-slate-700 truncate mt-0.5" data-testid="checkout-selected-device-imei">{imei}</div>
                            </div>
                          )}
                          {passcode && (
                            <div className="rounded-xl bg-white border border-slate-100 px-3 py-2 min-w-0">
                              <span className="text-slate-400 uppercase tracking-[0.12em] font-bold">Passcode</span>
                              <div className="font-mono text-slate-700 truncate mt-0.5" data-testid="checkout-selected-device-passcode">{passcode}</div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-4 space-y-1.5">
                        {problems.map((id) => {
                          const p = allProblems.find((x) => x.id === id) || PROBLEMS.find((x) => x.id === id);
                          const price = priceForProblem(id);
                          return (
                            <div key={id} className="flex items-center justify-between gap-3 text-sm rounded-xl bg-white border border-slate-100 px-3 py-2" data-testid={`checkout-selected-repair-${id}`}>
                              <span className="text-slate-700 truncate">{p?.label || id}</span>
                              <span className="font-mono font-semibold text-slate-800 shrink-0">${money(price)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!hasCurrentDraft && batch.length === 0 && (
                    <div className="text-sm text-slate-400 italic">No devices yet — go back and add one.</div>
                  )}

                  {/* + Add another device */}
                  <button
                    type="button"
                    onClick={parkCurrentDevice}
                    disabled={!hasCurrentDraft}
                    data-testid="wizard-add-another-device"
                    className="mt-4 w-full h-10 rounded-xl border-2 border-dashed text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      borderColor: "#10B981",
                      color: "#065F46",
                      background: "linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(16,185,129,0.02) 100%)",
                    }}
                  >
                    + Add another Device
                  </button>
                  <div className="border-t border-slate-200 mt-4 pt-3 flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600 font-semibold">
                      {accessories.length > 0 ? "Repair subtotal" : (batch.length > 0 ? "Repairs subtotal" : "Total")}
                    </div>
                    <div className="font-mono font-black text-2xl text-slate-900" data-testid="checkout-total">
                      ${money(batchSubtotal + (hasCurrentDraft ? estimated : 0))}
                    </div>
                  </div>

                  {(accessories.length > 0 || batch.length > 0) && (
                    <div className="mt-2 space-y-1">
                      {accessories.length > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Accessories subtotal</div>
                          <div className="font-mono font-semibold text-slate-700" data-testid="checkout-accessories-subtotal">
                            ${money(accessoriesTotal)}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-700 font-bold">Grand Total</div>
                        <div className="font-mono font-black text-3xl text-[#1E5FEB]" data-testid="checkout-grand-total">
                          ${money(checkoutTotal)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Auto-print label card removed — staff controls printing
                      defaults centrally from Settings → Printing. The wizard
                      still auto-prints when enabled there. */}

                  {/* Accessories — sell products alongside the repair on the same invoice */}
                  <div
                    className="mt-4 rounded-2xl border p-4"
                    style={{
                      background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 50%, #F0FDFA 100%)",
                      borderColor: "#A7F3D0",
                      boxShadow: "0 1px 2px rgba(16, 185, 129, 0.05), 0 8px 24px -8px rgba(16, 185, 129, 0.18)",
                    }}
                    data-testid="wizard-accessories-card"
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ background: "#10B981", boxShadow: "0 2px 6px rgba(16, 185, 129, 0.35)" }}
                        >
                          <ShoppingBag size={13} weight="bold" className="text-white" />
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#065F46" }}>
                            Add accessories
                          </div>
                          <div className="text-[10px] text-emerald-700/70 font-medium">
                            Sell on the same invoice
                          </div>
                        </div>
                      </div>
                      {accessories.length > 0 && (
                        <div
                          className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full"
                          style={{ background: "#D1FAE5", color: "#065F46" }}
                        >
                          {accessories.length} · ${money(accessoriesTotal)}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600/80" />
                      <input
                        value={accessorySearch}
                        onChange={(e) => { setAccessorySearch(e.target.value); setAccessorySearchOpen(true); }}
                        onFocus={() => setAccessorySearchOpen(true)}
                        onBlur={() => setTimeout(() => setAccessorySearchOpen(false), 150)}
                        placeholder="Search SKU or name · case, charger, screen protector…"
                        className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all"
                        style={{
                          borderColor: "#A7F3D0",
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
                        }}
                        onFocusCapture={(e) => { e.target.style.borderColor = "#10B981"; e.target.style.boxShadow = "0 0 0 3px rgba(16, 185, 129, 0.15)"; }}
                        onBlurCapture={(e) => { e.target.style.borderColor = "#A7F3D0"; e.target.style.boxShadow = "inset 0 1px 2px rgba(0,0,0,0.03)"; }}
                        data-testid="wizard-accessory-search"
                        autoComplete="off"
                      />
                      {accessorySearchOpen && accessorySearch.trim().length >= 1 && accessoryResults.length > 0 && (
                        <div
                          className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl border overflow-hidden z-30 max-h-64 overflow-y-auto"
                          style={{
                            borderColor: "#A7F3D0",
                            boxShadow: "0 14px 32px rgba(16, 185, 129, 0.18)",
                          }}
                          data-testid="wizard-accessory-results"
                        >
                          {accessoryResults.map((p) => {
                            const outOfStock = Number(p.stock || 0) <= 0;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                disabled={outOfStock}
                                onMouseDown={(e) => { e.preventDefault(); if (!outOfStock) addAccessory(p); }}
                                data-testid={`wizard-accessory-opt-${p.id}`}
                                className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-t border-emerald-100/70 first:border-t-0 transition-colors ${
                                  outOfStock
                                    ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                    : "bg-white hover:bg-emerald-50/60"
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="text-[13px] font-semibold text-slate-800 truncate">{p.name}</div>
                                  <div className="text-[10px] font-mono text-slate-500 truncate">
                                    {p.sku} · {outOfStock ? <span className="text-rose-600 font-bold">OUT OF STOCK</span> : <span>stock {p.stock}</span>}
                                  </div>
                                </div>
                                <div className="text-[13px] font-mono font-bold tabular-nums shrink-0 text-emerald-700">
                                  ${money(p.price)}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {accessories.length > 0 && (
                      <div className="mt-3 space-y-1.5" data-testid="wizard-accessories-list">
                        {accessories.map((a) => (
                          <div
                            key={a.product_id}
                            className="flex items-center gap-2 rounded-xl bg-white/80 backdrop-blur-sm border px-2.5 py-2 transition-colors hover:bg-white"
                            style={{ borderColor: "#BBF7D0" }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold text-slate-800 truncate">{a.name}</div>
                              <div className="text-[10px] font-mono text-slate-500 truncate">{a.sku}</div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => bumpAccessoryQty(a.product_id, -1)}
                                data-testid={`wizard-accessory-dec-${a.product_id}`}
                                className="w-7 h-7 rounded-md bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-800 font-bold transition-colors"
                              >−</button>
                              <div className="w-7 text-center text-[13px] font-mono font-bold tabular-nums text-slate-900">{a.quantity}</div>
                              <button
                                type="button"
                                onClick={() => bumpAccessoryQty(a.product_id, 1)}
                                data-testid={`wizard-accessory-inc-${a.product_id}`}
                                className="w-7 h-7 rounded-md bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-800 font-bold transition-colors"
                              >+</button>
                            </div>
                            {/* Line total — click to edit. This IS the price the
                                customer pays for this line. We divide by qty
                                internally to keep unit_price in sync. */}
                            {editingPriceId === a.product_id ? (
                              <div className="flex items-center gap-0.5 shrink-0" style={{ color: "#065F46" }}>
                                <span className="text-[13px] font-mono font-bold">$</span>
                                <input
                                  autoFocus
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingPriceValue}
                                  onChange={(e) => setEditingPriceValue(e.target.value)}
                                  onBlur={() => commitEditPrice(a.product_id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); commitEditPrice(a.product_id); }
                                    else if (e.key === "Escape") { setEditingPriceId(""); setEditingPriceValue(""); }
                                  }}
                                  data-testid={`wizard-accessory-price-input-${a.product_id}`}
                                  className="w-20 h-7 px-1 text-right text-[13px] font-mono font-bold tabular-nums bg-white border border-emerald-400 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingPriceId(a.product_id);
                                  // Pre-fill with the current LINE TOTAL (qty × unit)
                                  setEditingPriceValue(Number(a.unit_price * a.quantity).toFixed(2));
                                }}
                                data-testid={`wizard-accessory-price-edit-${a.product_id}`}
                                title="Click to edit this price"
                                className="w-20 text-right text-[13px] font-mono font-bold tabular-nums shrink-0 hover:bg-emerald-100/70 hover:text-emerald-700 rounded px-1.5 py-0.5 transition-colors cursor-text"
                                style={{ color: "#065F46" }}
                              >
                                ${money(a.unit_price * a.quantity)}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeAccessory(a.product_id)}
                              data-testid={`wizard-accessory-remove-${a.product_id}`}
                              className="w-6 h-6 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
                              title="Remove"
                            ><X size={12} weight="bold" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="rounded-[20px] border border-slate-200 bg-white overflow-hidden shadow-sm shadow-slate-200/60" data-testid="checkout-payment-card">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center">
                        <CreditCard size={15} weight="bold" className="text-[#1E5FEB]" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600 font-semibold">Payment</div>
                        <div className="text-[11px] text-slate-400">Collect now or create the ticket only</div>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" data-testid="checkout-collect-payment-toggle">
                      <input
                        type="checkbox"
                        checked={collectPayment}
                        onChange={(e) => setCollectPayment(e.target.checked)}
                        className="accent-[#1E5FEB] w-4 h-4"
                        data-testid="wizard-collect-payment"
                      />
                      Collect now
                    </label>
                  </div>
                  {/*
                    Keep the section visually dimmed when the "Collect now"
                    checkbox is off, but allow clicks through — touching any
                    payment control auto-enables collection. Matches POS
                    muscle-memory and removes the dead-click trap where CASH /
                    CARD / OTHER looked active but silently ignored presses.
                  */}
                  <div className={`p-5 space-y-4 ${collectPayment ? "" : "opacity-55"}`}>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label>Method</Label>
                        <button
                          type="button"
                          onClick={() => {
                            const next = !splitMode;
                            setSplitMode(next);
                            if (!collectPayment) setCollectPayment(true);
                            if (next) {
                              const cur = Number(tendered || 0);
                              setSplitCash(cur > 0 ? cur.toFixed(2) : "");
                              setSplitCard(""); setSplitTyro(""); setSplitOther(""); setSplitStripe("");
                            } else {
                              setSplitCash(""); setSplitCard(""); setSplitTyro(""); setSplitOther(""); setSplitStripe("");
                            }
                          }}
                          data-testid="checkout-split-toggle"
                          className={`text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full transition-colors border ${
                            splitMode
                              ? "border-[#1E5FEB] bg-[#1E5FEB] text-white"
                              : "border-slate-200 text-slate-600 hover:border-[#1E5FEB] hover:text-[#1E5FEB]"
                          }`}
                          title="Pay with multiple methods on one ticket"
                        >
                          {splitMode ? "Split on" : "Split"}
                        </button>
                      </div>
                      {!splitMode && (() => {
                        // Only require tyro_enabled — the integration_key is
                        // re-fetched from `/stores/{id}/tyro/sdk-key` at click
                        // time so a missing/empty key surfaces inside the
                        // TyroPaymentDialog instead of silently hiding the
                        // button. Mirrors the user expectation: "if I turned
                        // Tyro on here, I should see the button here."
                        const tyroOn = !!currentStore?.tyro_enabled;
                        const methods = tyroOn
                          ? ["cash", "tyro", "stripe", "other"]
                          : ["cash", "stripe", "other"];
                        return (
                          <div className={`grid gap-2 mt-2 grid-cols-2 sm:grid-cols-${methods.length <= 3 ? "3" : "4"}`} data-testid="checkout-payment-method-grid">
                            {methods.map((m) => {
                              if (m === "tyro") {
                                const active = paymentMethod === "tyro" && collectPayment;
                                return (
                                  <button
                                    key="tyro"
                                    type="button"
                                    onClick={() => { setPaymentMethod("tyro"); if (!collectPayment) setCollectPayment(true); }}
                                    data-testid="checkout-pay-tyro"
                                    title="Charge to a paired Tyro EFTPOS terminal"
                                    className={`h-12 rounded-xl text-[11px] tracking-wider font-semibold border transition-all flex items-center justify-center gap-1.5 group ${
                                      active
                                        ? "bg-[#1E5FEB] text-white border-[#1E5FEB] shadow-[0_4px_14px_rgba(30,95,235,0.25)]"
                                        : "bg-white border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white"
                                    }`}
                                  >
                                    <img
                                      src="/tyro-logo.gif"
                                      alt="Tyro"
                                      className={`h-4 w-auto transition-all ${active ? "brightness-0 invert" : "group-hover:brightness-0 group-hover:invert"}`}
                                    />
                                    <span>EFTPOS</span>
                                  </button>
                                );
                              }
                              if (m === "stripe") {
                                const active = paymentMethod === "stripe" && collectPayment;
                                return (
                                  <button
                                    key="stripe"
                                    type="button"
                                    onClick={() => { setPaymentMethod("stripe"); if (!collectPayment) setCollectPayment(true); }}
                                    data-testid="checkout-pay-stripe"
                                    title="Send a Stripe payment link to the customer"
                                    className={`h-12 rounded-xl text-[11px] tracking-wider font-semibold border transition-all flex items-center justify-center gap-1.5 ${
                                      active
                                        ? "bg-[#635BFF] text-white border-[#635BFF] shadow-[0_4px_14px_rgba(99,91,255,0.25)]"
                                        : "bg-white border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white"
                                    }`}
                                  >
                                    <StripeMark className="h-4 w-auto shrink-0" />
                                    STRIPE
                                  </button>
                                );
                              }
                              return (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => { setPaymentMethod(m); if (!collectPayment) setCollectPayment(true); }}
                                  data-testid={`checkout-pay-${m}`}
                                  className={`h-12 rounded-xl text-[11px] tracking-wider font-semibold border transition-all flex items-center justify-center gap-1.5 ${
                                    paymentMethod === m && collectPayment
                                      ? "bg-slate-900 text-white border-slate-900"
                                      : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"
                                  }`}
                                >
                                  {m === "cash" && <Money size={14} weight="bold" />}
                                  {m === "other" && <Wallet size={14} weight="bold" />}
                                  {m.toUpperCase()}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {!splitMode && collectPayment && paymentMethod === "other" && currentStore?.tyro_enabled && (
                        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="checkout-other-tyro-hint">
                          Paying by card? Use <strong>EFTPOS</strong> so it&apos;s charged and recorded as Tyro. &quot;Other&quot; is for non-card payments only.
                        </div>
                      )}
                    </div>

                    {!splitMode ? (
                      paymentMethod === "tyro" && collectPayment ? (
                        /* Minimal "ready to charge" card — just the amount
                           and a small Tyro-branded label. No gradient,
                           no header bar, no CTA footer — the action
                           button below already says CHARGE TYRO & CREATE. */
                        <div
                          className="rounded-2xl border border-[#1E5FEB]/20 bg-white px-5 py-5 flex items-center justify-between gap-4"
                          data-testid="checkout-tyro-card"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[#1E5FEB] text-[10px] uppercase tracking-[0.18em] font-bold mb-1">
                              <img src="/tyro-logo.gif" alt="" className="h-3 w-auto" />
                              <span>EFTPOS · charging</span>
                            </div>
                            <div className="text-[12px] text-slate-500 leading-snug">
                              Sent to the paired terminal on tap.
                            </div>
                          </div>
                          {(() => {
                            const total = Number(checkoutTotal || 0);
                            const dollars = Math.floor(total);
                            const cents = Math.round((total - dollars) * 100).toString().padStart(2, "0");
                            return (
                              <div className="font-display font-bold tabular-nums leading-none text-[#121216] flex items-start shrink-0">
                                <span className="text-slate-400 text-[18px] font-semibold mt-1 mr-0.5">$</span>
                                <span className="text-[36px] tracking-tight">{dollars.toLocaleString()}</span>
                                <span className="text-slate-400 text-[18px] font-semibold mt-1 ml-0.5">.{cents}</span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : paymentMethod === "stripe" && collectPayment ? (
                        /* Stripe online invoice card — customer pays remotely */
                        <div
                          className="rounded-2xl border border-[#635BFF]/20 bg-white px-5 py-5 flex items-center justify-between gap-4"
                          data-testid="checkout-stripe-card"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[#635BFF] text-[10px] uppercase tracking-[0.18em] font-bold mb-1">
                              <span>STRIPE · online invoice</span>
                            </div>
                            <div className="text-[12px] text-slate-500 leading-snug">
                              {(() => {
                                const cust = customers.find((c) => c.id === customerId);
                                const hasContact = cust?.phone || cust?.email;
                                return hasContact
                                  ? `Payment link sent to ${cust.phone || cust.email} on create.`
                                  : "Payment link created — copy and share with customer.";
                              })()}
                            </div>
                          </div>
                          {(() => {
                            const total = Number(checkoutTotal || 0);
                            const dollars = Math.floor(total);
                            const cents = Math.round((total - dollars) * 100).toString().padStart(2, "0");
                            return (
                              <div className="font-display font-bold tabular-nums leading-none text-[#121216] flex items-start shrink-0">
                                <span className="text-slate-400 text-[18px] font-semibold mt-1 mr-0.5">$</span>
                                <span className="text-[36px] tracking-tight">{dollars.toLocaleString()}</span>
                                <span className="text-slate-400 text-[18px] font-semibold mt-1 ml-0.5">.{cents}</span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div>
                          <Label>Amount tendered</Label>
                          <input
                            type="number" min="0" step="0.01"
                            value={tendered}
                            onChange={(e) => { setTendered(e.target.value); if (!collectPayment && e.target.value) setCollectPayment(true); }}
                            placeholder="0.00"
                            className="w-full h-14 px-4 font-mono text-2xl text-right rounded-2xl border border-slate-200 bg-white focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none shadow-sm"
                            data-testid="checkout-tendered"
                          />
                          <div className="flex gap-1.5 mt-2" data-testid="checkout-quick-tender-buttons">
                            <button type="button" onClick={() => { setTendered(String(checkoutTotal)); if (!collectPayment) setCollectPayment(true); }} data-testid="checkout-tender-exact" className="flex-1 h-9 rounded-lg bg-[#1E5FEB] hover:bg-[#1849C8] text-xs font-semibold text-white">EXACT</button>
                            {[20, 50, 100, 200].map((v) => (
                              <button key={v} type="button" onClick={() => { setTendered(String(v)); if (!collectPayment) setCollectPayment(true); }} data-testid={`checkout-tender-${v}`} className="flex-1 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-mono font-semibold text-slate-700">${v}</button>
                            ))}
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Split tender</Label>
                          <button
                            type="button"
                            onClick={() => {
                              const sum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0);
                              const remaining = Math.max(0, checkoutTotal - sum);
                              if (remaining <= 0) return;
                              if (!collectPayment) setCollectPayment(true);
                              const tyroOn = !!currentStore?.tyro_enabled;
                              if (!splitCard && !splitTyro && !splitOther) {
                                if (tyroOn) setSplitTyro(remaining.toFixed(2));
                                else setSplitCard(remaining.toFixed(2));
                              } else if (!splitTyro && tyroOn) setSplitTyro(remaining.toFixed(2));
                              else if (!splitCard) setSplitCard(remaining.toFixed(2));
                              else if (!splitOther) setSplitOther(remaining.toFixed(2));
                              else setSplitCash((Number(splitCash || 0) + remaining).toFixed(2));
                            }}
                            data-testid="checkout-split-balance"
                            className="text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white transition-colors"
                          >
                            Auto-balance
                          </button>
                        </div>
                        <p className="text-[10px] text-slate-400 leading-relaxed">
                          Tyro charges its slice now on the terminal. Stripe sends a payment link instead —
                          only one of the two can be used per split.
                        </p>
                        {[
                          { key: "cash", label: "CASH", val: splitCash, set: setSplitCash, show: true },
                          { key: "tyro", label: "TYRO", val: splitTyro, set: setSplitTyro, show: !!currentStore?.tyro_enabled, mutexWith: "stripe" },
                          { key: "other", label: "OTHER", val: splitOther, set: setSplitOther, show: true },
                          { key: "stripe", label: "STRIPE", val: splitStripe, set: setSplitStripe, show: true, deferred: true, mutexWith: "tyro" },
                        ].filter((r) => r.show).map((row) => {
                          const mutexVal = row.mutexWith === "stripe" ? splitStripe : row.mutexWith === "tyro" ? splitTyro : "";
                          const mutexLocked = row.mutexWith && Number(mutexVal || 0) > 0;
                          return (
                          <div key={row.key} className="flex items-center gap-2">
                            <div className={`w-[68px] text-[11px] tracking-wider font-semibold uppercase shrink-0 flex items-center gap-1 ${mutexLocked ? "text-slate-300" : "text-slate-600"}`}>
                              {row.key === "tyro" && <img src="/tyro-logo.gif" alt="" className={`h-3 w-auto ${mutexLocked ? "opacity-30" : ""}`} />}
                              {row.key === "stripe" && <StripeMark className={`h-3.5 w-auto ${mutexLocked ? "opacity-30" : "text-[#635BFF]"}`} />}
                              {row.key === "cash" && <Money size={12} weight="bold" />}
                              {row.key === "other" && <Wallet size={12} weight="bold" />}
                              {row.label}
                            </div>
                            <div className="flex-1 relative" title={mutexLocked ? `Can't combine with ${row.mutexWith === "stripe" ? "Stripe" : "Tyro"} in the same split — clear it first` : undefined}>
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-[13px]">$</span>
                              <input
                                type="number" min="0" step="0.01"
                                value={row.val}
                                disabled={mutexLocked}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  row.set(v);
                                  if (!collectPayment && v) setCollectPayment(true);
                                  // Tyro (real-time terminal charge) and Stripe (deferred payment
                                  // link) can't both be in flight for the same split — as soon as
                                  // one gets an amount, clear the other rather than letting a
                                  // stale value silently ride along disabled.
                                  if (row.mutexWith === "stripe" && Number(v || 0) > 0) setSplitStripe("");
                                  else if (row.mutexWith === "tyro" && Number(v || 0) > 0) setSplitTyro("");
                                }}
                                placeholder={mutexLocked ? "locked" : "0.00"}
                                data-testid={`checkout-split-${row.key}`}
                                className={`w-full h-11 pl-7 pr-3 font-mono text-right rounded-xl border tabular-nums transition-colors ${
                                  mutexLocked
                                    ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                                    : "border-slate-200 bg-white focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none"
                                }`}
                              />
                            </div>
                            {row.deferred && Number(row.val || 0) > 0 && (
                              <span className="text-[10px] text-[#635BFF] font-semibold shrink-0 w-16">deposit link</span>
                            )}
                          </div>
                          );
                        })}
                        <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 pt-1">
                          <span>Sum tendered {Number(splitStripe || 0) > 0 && <span className="text-[#635BFF]">(+ stripe link)</span>}</span>
                          <span data-testid="checkout-split-sum" className="tabular-nums font-semibold text-slate-900">
                            ${money(Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0) + Number(splitStripe || 0))}
                            {Math.round((Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0) + Number(splitStripe || 0)) * 100) === Math.round(checkoutTotal * 100) && checkoutTotal > 0 && (
                              <span className="ml-1.5 text-emerald-600">✓</span>
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                      {(() => {
                        const splitSum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0) + Number(splitStripe || 0);
                        const tenderedN = splitMode ? splitSum : Number(tendered || 0);
                        const isDeposit = collectPayment && tenderedN > 0 && tenderedN + 1e-6 < checkoutTotal;
                        const bal = Math.max(0, checkoutTotal - tenderedN);
                        const stripeSlice = splitMode ? Number(splitStripe || 0) : 0;
                        if (isDeposit) {
                          return (
                            <div
                              className="flex-1 flex items-center justify-between px-3 py-2 rounded-xl border-2"
                              style={{
                                background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)",
                                borderColor: "#DC2626",
                                boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.08)",
                              }}
                              data-testid="checkout-balance-due"
                            >
                              <div>
                                <span className="text-[10px] uppercase tracking-[0.18em] text-[#DC2626] font-bold">BALANCE DUE</span>
                                <div className="text-[10px] font-mono text-[#7F1D1D] mt-0.5">
                                  Deposit · ${money(tenderedN)}
                                  {stripeSlice > 0 && <span className="text-[#635BFF]"> (${money(stripeSlice)} via stripe link)</span>}
                                </div>
                              </div>
                              <span className="font-mono font-bold text-xl text-[#DC2626] tabular-nums">${money(bal)}</span>
                            </div>
                          );
                        }
                        return (
                          <>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">CHANGE DUE</div>
                            <div className="font-mono font-bold text-xl text-slate-900" data-testid="checkout-change">
                              ${money(splitMode || paymentMethod === "cash" ? Math.max(0, tenderedN - checkoutTotal) : 0)}
                            </div>
                            {!splitMode && paymentMethod !== "cash" && tenderedN > checkoutTotal + 0.005 && (
                              <div className="text-[11px] text-amber-700 mt-1" data-testid="checkout-noncash-capped">Only cash gives change — ${money(checkoutTotal)} will be recorded</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </Step>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-4 sm:px-7 py-4 border-t border-slate-200 bg-white/95 backdrop-blur-md shadow-[0_-12px_24px_rgba(15,23,42,0.06)]">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="h-11 px-4 rounded-full border border-slate-200 hover:border-slate-400 hover:bg-slate-50 text-[13px] font-semibold flex items-center gap-2 text-slate-700 bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="wizard-back"
          >
            <CaretLeft size={14} weight="bold" /> BACK
          </button>

          <div className="hidden sm:flex items-center gap-4 rounded-full bg-slate-100 border border-slate-200 px-4 py-2" data-testid="wizard-footer-summary">
            <div className="font-mono text-[11px] text-slate-500 tracking-wider">
              {step + 1} / {STEPS.length}
            </div>
            <div className="h-5 w-px bg-slate-300" />
            <div className="flex items-center gap-2" data-testid="wizard-footer-estimate">
              <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Estimate</span>
              <span className="font-mono text-sm font-bold text-slate-950 tabular-nums">${money(estimated)}</span>
            </div>
          </div>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => canNext && setStep((s) => s + 1)}
              onKeyUp={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.currentTarget.blur();
                }
              }}
              disabled={!canNext}
              className="h-11 px-6 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 hover:from-[#1E5FEB] hover:to-[#3B82F6] disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white text-[13px] font-semibold flex items-center gap-2 transition-all shadow-md hover:shadow-lg hover:shadow-blue-200 disabled:shadow-none active:scale-95"
              data-testid="wizard-next"
            >
              {step === 4 && !customerId ? "SELECT CUSTOMER" : step === 4 && showNewCust ? "SAVE CUSTOMER" : "CONTINUE"} <CaretRight size={14} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              onClick={async () => {
                // Block any clicks (incl. accidental Enter / Space
                // chained from the step-4 → step-5 transition) during
                // the 700ms arm window. See `finishArmed` useEffect.
                if (!finishArmed) return;
                const splitSum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0);
                const splitStripeAmt = splitMode ? Number(splitStripe || 0) : 0;
                const tenderedN = splitMode ? splitSum : Number(tendered || 0);
                const isTyroSingle = collectPayment && !splitMode && paymentMethod === "tyro";
                const isStripeSingle = collectPayment && !splitMode && paymentMethod === "stripe";
                // For Tyro single-tender, the SDK charges the FULL total —
                // never a deposit — so skip the deposit confirmation here.
                // For split-tender with a Tyro slice, submitCheckout opens
                // the SDK first; we let it handle deposit confirmation
                // after the slice approves. Same for a Stripe slice — it
                // explains the "shortfall" on its own (payment link going
                // out, not an accidental short payment) — ANY stripe
                // amount skips this, not just one covering the full rest.
                const hasStripeSliceFooter = splitMode && splitStripeAmt > 0;
                if (!isTyroSingle && !isStripeSingle && !hasStripeSliceFooter && collectPayment && tenderedN + 1e-6 < checkoutTotal && tenderedN > 0 && !(splitMode && Number(splitTyro || 0) > 0)) {
                  const bal = checkoutTotal - tenderedN;
                  const ok = window.confirm(
                    `Take a deposit of $${tenderedN.toFixed(2)}?\n\nBalance due: $${bal.toFixed(2)}\n\nThe customer can settle the balance later from the invoice page.`
                  );
                  if (!ok) return;
                }
                (collectPayment ? (paymentMethod === "stripe" && !splitMode ? submitStripeInvoice : submitCheckout) : submitTicketOnly)();
              }}
              disabled={(() => {
                if (!finishArmed) return true;
                if (submitting || !customerId) return true;
                if (!collectPayment) return false;
                if (checkoutTotal <= 0) return false;
                if (paymentMethod === "tyro" && !splitMode) return false; // Tyro charges full total via SDK
                if (paymentMethod === "stripe" && !splitMode) return false; // Stripe sends remote invoice
                if (splitMode) {
                  const splitSum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0) + Number(splitStripe || 0);
                  return splitSum <= 0;
                }
                return Number(tendered || 0) <= 0;
              })()}
              className={`h-11 px-6 rounded-full disabled:opacity-40 disabled:from-slate-300 disabled:to-slate-400 text-white text-[13px] font-semibold flex items-center gap-2 transition-all shadow-md ${
                collectPayment && paymentMethod === "stripe" && !splitMode
                  ? "bg-gradient-to-r from-[#635BFF] to-[#5148e0] hover:from-[#5148e0] hover:to-[#4338ca] shadow-purple-500/25"
                  : collectPayment && Number(tendered || 0) > 0 && Number(tendered || 0) + 1e-6 < checkoutTotal
                    ? "bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#991B1B] shadow-red-500/25"
                    : "bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] shadow-blue-500/25"
              }`}
              data-testid="wizard-pay-and-create"
            >
              {submitting ? (
                <>PROCESSING…</>
              ) : collectPayment ? (
                paymentMethod === "tyro" && !splitMode
                  ? <>CHARGE TYRO & CREATE <CheckCircle size={14} weight="bold" /></>
                  : paymentMethod === "stripe" && !splitMode
                    ? <>SEND INVOICE & CREATE <PaperPlaneTilt size={14} weight="bold" /></>
                    : splitMode && Number(splitTyro || 0) > 0
                      ? <>CHARGE TYRO & CREATE <CheckCircle size={14} weight="bold" /></>
                      : splitMode && Number(splitStripe || 0) > 0
                        ? <>SEND LINK & CREATE <PaperPlaneTilt size={14} weight="bold" /></>
                        : Number(tendered || 0) > 0 && Number(tendered || 0) + 1e-6 < checkoutTotal
                          ? <>TAKE DEPOSIT & CREATE <CheckCircle size={14} weight="bold" /></>
                          : <>PAY & CREATE <CheckCircle size={14} weight="bold" /></>
              ) : (
                <>
                  {printOnCreate && prefs?.label_printer_id ? (
                    <>CREATE & PRINT LABEL <Printer size={14} weight="bold" /></>
                  ) : (
                    <>CREATE TICKET <CheckCircle size={14} weight="bold" /></>
                  )}
                </>
              )}
            </button>
          )}
        </div>
      </div>
      {catalogEditor && (
        <CatalogScopeDrawer
          editor={catalogEditor}
          setEditor={setCatalogEditor}
          saving={catalogSaving}
          isAdmin={isAdmin}
          brandOptions={brandEditorOptions}
          getDeviceOptions={getDeviceOptionsForBrand}
          getModelOptions={getModelOptionsForScope}
          onClose={() => setCatalogEditor(null)}
          onSave={saveCatalogEditor}
          onDelete={deleteCatalogEditor}
        />
      )}
    </div>
    )}

    {/* Tyro EFTPOS — single tender for the FULL sale total. On approval the
        SDK callback re-enters submitCheckout(tyroMeta) which routes the
        payment as card_manual + tyro_meta sidecar (server constraint).
        We deliberately DO NOT close the dialog inside onApproved — the
        dialog has already transitioned to its APPROVED state (green
        check + confetti + "Saving sale…") and unmounting it now would
        rob the cashier of the success animation the POS Tyro checkout
        also shows. Instead we await submitCheckout and close the dialog
        from a finally{} so the green screen stays visible while the
        sale is created server-side; on error the dialog still unmounts
        so the cashier isn't stuck. */}
    {tyroOpen && (
      <TyroPaymentDialog
        mode="purchase"
        amountCents={Math.round((() => {
          const taxRate = Number(currentStore?.tax_rate || 0) / 100;
          const taxInclusive = currentStore?.tax_inclusive !== false;
          return taxInclusive ? checkoutTotal : checkoutTotal * (1 + taxRate);
        })() * 100)}
        onClose={() => {
          setTyroOpen(false);
          // If the sale was already saved (the cashier tapped Done from
          // inside the SAVED morph), close the WHOLE wizard via the
          // parent's onClose — otherwise the wizard form pops back up
          // with all the previous ticket's state, the next ticket
          // inherits the old customer + device, and the cashier can
          // accidentally re-print a paid ticket on top of itself.
          // If postSale is still null, the cashier cancelled mid-flow
          // and should stay on the Finish step to retry.
          if (postSale) {
            handlePostSaleClose();
          }
        }}
        onApproved={async (meta) => {
          await submitCheckout(meta);
          // Don't close — dialog will morph to SAVED via `completedSale`.
        }}
        saveError={tyroSaveError}
        onRetrySave={() => lastTyroMetaRef.current && submitCheckout(lastTyroMetaRef.current)}
        completedSale={postSale?.sale || null}
        customer={postSale?.customer || null}
        repairTicket={postSale?.ticket || null}
        storeForActions={currentStore}
      />
    )}

    {/* Tyro EFTPOS — split-tender slice. Same "keep dialog open until
        sale POSTs" rule applies — see comment above tyroOpen. */}
    {tyroSplitOpen && (
      <TyroPaymentDialog
        mode="purchase"
        amountCents={Math.round(Number(splitTyro || 0) * 100)}
        onClose={() => {
          setTyroSplitOpen(false);
          if (postSale) {
            handlePostSaleClose();
          }
        }}
        onApproved={async (meta) => {
          await submitCheckout(meta);
          // Don't close — dialog will morph to SAVED via `completedSale`.
        }}
        saveError={tyroSaveError}
        onRetrySave={() => lastTyroMetaRef.current && submitCheckout(lastTyroMetaRef.current)}
        completedSale={postSale?.sale || null}
        customer={postSale?.customer || null}
        repairTicket={postSale?.ticket || null}
        storeForActions={currentStore}
      />
    )}

    {/* Unified Sale Complete card — standalone overlay for non-Tyro
        paths (cash / card / other). For Tyro flows the same body is
        rendered INSIDE the TyroPaymentDialog above (mode="inline"),
        so we suppress this overlay when a Tyro dialog is up. */}
    <SaleCompleteCard
      open={!!postSale && !tyroOpen && !tyroSplitOpen}
      onClose={handlePostSaleClose}
      sale={postSale?.sale}
      customer={postSale?.customer}
      store={currentStore}
      repairTicket={postSale?.ticket}
      stripeDepositAmount={postSale?.stripeDepositAmount || 0}
    />

    <PostTicketActionsModal
      open={!!postTicket}
      onClose={() => { setPostTicket(null); onClose?.(); }}
      ticket={postTicket?.ticket}
      tickets={postTicket?.tickets}
      customer={postTicket?.customer}
      store={currentStore}
    />
    </>
  );
}

// ---------- Helpers ----------
const inputCls =
  "w-full h-11 px-3 rounded-2xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white transition-[border-color,box-shadow,background-color]";

/**
 * Shared reorder toolbar — the same "wiggle mode" affordance used for the
 * Model step, now generalized so the Manufacturer / Device-type / Problem
 * steps can share the exact same pattern. The parent owns the ``reorder``
 * flag + the ``onDone`` persist callback so each list can be reordered
 * independently without losing its draft state.
 */
function WiggleToolbar({ count, reorder, setReorder, onDone, testidPrefix = "list" }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <div className="text-[11px] text-slate-500 font-mono">
        {reorder
          ? <span className="text-[#1E5FEB] font-semibold">Drag to reorder · release to snap into place</span>
          : `${count} options`}
      </div>
      {reorder ? (
        <button
          type="button"
          onClick={onDone}
          data-testid={`${testidPrefix}-reorder-done`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11px] font-bold tracking-wider"
        >
          <Check size={12} weight="bold" /> DONE
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setReorder(true)}
          data-testid={`${testidPrefix}-reorder-toggle`}
          title="Reorder"
          className="inline-flex items-center gap-1 h-8 px-2 rounded-full border border-slate-200 hover:border-[#121216] hover:text-[#121216] text-slate-500 text-[11px] font-semibold"
        >
          <DotsThreeVertical size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}


function Step({ title, subtitle, children }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h3 className="font-heading font-bold text-[24px] tracking-tight text-slate-950 leading-tight">{title}</h3>
          {subtitle && <div className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function OptionCard({ selected, onClick, children, testid, draggable, onDragStart, onDragOver, onDragEnd, dragClass }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick && onClick();
        }
      }}
      data-testid={testid}
      draggable={!!draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={`w-full h-full min-h-[112px] flex flex-col items-start p-4 rounded-[18px] border text-left transition-[background-color,border-color,box-shadow,transform] duration-200 active:scale-[0.98] select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5FEB] focus-visible:ring-offset-2 ${
        selected
          ? "bg-gradient-to-br from-blue-50 to-white border-[#1E5FEB] text-slate-950 ring-2 ring-blue-100 shadow-md shadow-blue-100/70"
          : "bg-white border-slate-200 hover:border-[#1E5FEB]/40 hover:bg-blue-50/40 hover:shadow-md hover:-translate-y-0.5 text-slate-700"
      } ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${dragClass || ""}`}
    >
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
      {children}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      {children}
    </label>
  );
}

function SummaryRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`${mono ? "font-mono" : "font-semibold"} text-slate-800`}>{value || "—"}</div>
    </div>
  );
}

function InlineAdd({ label, placeholder, value, onChange, onSave, onCancel, testid }) {
  return (
    <div className="mt-4 rounded-[18px] border border-blue-200 bg-blue-50/60 p-3.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E5FEB] font-semibold mb-2">{label}</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputCls + " flex-1"}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(); } }}
          data-testid={`${testid}-input`}
        />
        <button type="button" onClick={onSave} data-testid={`${testid}-save-button`} className="h-10 px-4 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-semibold">ADD</button>
        <button type="button" onClick={onCancel} data-testid={`${testid}-cancel-button`} className="h-10 px-3 rounded-xl border border-slate-200 bg-white hover:border-slate-400 text-[12px] font-semibold text-slate-600">CANCEL</button>
      </div>
      <div className="text-[11px] text-slate-500 mt-2">Saved to your catalog for next time.</div>
    </div>
  );
}


function CatalogQuickActions({ testidPrefix, onEdit, inline = false, icon = "pencil" }) {
  const cls = inline
    ? "inline-flex items-center gap-1"
    : "absolute top-2 right-2 z-10 flex items-center gap-1 opacity-95";
  const Icon = icon === "gear" ? GearSix : PencilSimple;
  return (
    <div className={cls} data-testid={`${testidPrefix}-actions`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit && onEdit(); }}
        data-testid={`${testidPrefix}-edit-button`}
        title={icon === "gear" ? "Edit details / scope" : "Edit"}
        className="h-7 w-7 rounded-lg border border-dashed border-violet-300 bg-violet-50/60 text-violet-500 hover:border-violet-500 hover:bg-violet-100 hover:text-violet-700 shadow-sm flex items-center justify-center transition-colors"
      >
        <Icon size={12} weight="bold" />
      </button>
    </div>
  );
}

function CatalogScopeDrawer({ editor, setEditor, saving, isAdmin, brandOptions, getDeviceOptions, getModelOptions, onClose, onSave, onDelete }) {
  const title = editor.type === "device_type" ? "Edit device type" : editor.type === "model" ? "Edit model" : "Edit problem";
  const brandId = editor.parent_manufacturer || "";
  const deviceId = editor.parent_device_type || "";
  const deviceOptions = brandId ? getDeviceOptions(brandId) : [];
  const modelOptions = brandId && deviceId ? getModelOptions(brandId, deviceId) : [];
  const update = (patch) => setEditor((prev) => ({ ...prev, ...patch }));
  const defaultBrandId = editor.parent_manufacturer || editor.current_brand_id || brandOptions[0]?.id || "";
  const defaultDeviceId = (editor.parent_device_type && editor.parent_device_type !== "all") ? editor.parent_device_type : (editor.current_device_type_id || "");
  const defaultModelId = editor.parent_model || editor.current_model_id || "";
  const applyScopeMode = (mode) => {
    if (mode === "all") {
      update({ scope_mode: "all", parent_manufacturer: "", parent_device_type: "all", parent_model: "" });
      return;
    }
    if (mode === "brand") {
      update({ scope_mode: "brand", parent_manufacturer: defaultBrandId, parent_device_type: "", parent_model: "" });
      return;
    }
    if (mode === "device") {
      update({ scope_mode: "device", parent_manufacturer: defaultBrandId, parent_device_type: defaultDeviceId, parent_model: "" });
      return;
    }
    update({ scope_mode: "model", parent_manufacturer: defaultBrandId, parent_device_type: defaultDeviceId, parent_model: defaultModelId });
  };
  const scopeOptions = editor.type === "device_type"
    ? [
        { id: "all", title: "All brands", sub: "Show this device type for every brand" },
        { id: "brand", title: "Selected brand", sub: "Show only under one brand" },
      ]
    : editor.type === "model"
      ? [
          { id: "all", title: "All devices", sub: "Show this model option everywhere" },
          { id: "device", title: "Selected device type", sub: "Example: only Apple → iPhone" },
        ]
      : [
          { id: "all", title: "All devices", sub: "Show this repair option everywhere" },
          { id: "device", title: "All models in device type", sub: "Example: all Apple → iPhone models" },
          { id: "model", title: "Particular model", sub: "Example: only iPhone 15 Pro" },
        ];
  return (
    <div className="absolute inset-0 z-50 flex justify-end bg-slate-950/35 backdrop-blur-[1px]" data-testid="wizard-catalog-editor-overlay">
      <aside className="h-full w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col" data-testid="wizard-catalog-editor-drawer">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E5FEB] font-bold">Wizard catalog</div>
            <h3 className="mt-1 text-xl font-heading font-bold text-slate-950">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="wizard-catalog-editor-close"
            className="h-9 w-9 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 flex items-center justify-center"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          <label className="block">
            <Label>Name</Label>
            <input
              value={editor.label}
              onChange={(e) => update({ label: e.target.value })}
              className={inputCls}
              data-testid="wizard-catalog-editor-name"
              autoFocus
            />
          </label>

          {editor.type === "problem" && (
            <label className="block">
              <Label>Default price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editor.price ?? 0}
                  onChange={(e) => update({ price: e.target.value })}
                  className={inputCls + " pl-7 font-mono text-right"}
                  data-testid="wizard-catalog-editor-price"
                />
              </div>
            </label>
          )}

          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 space-y-3" data-testid="wizard-catalog-editor-scope">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-slate-900">Show in wizard</div>
                <div className="text-xs text-slate-500">Choose exactly where this item appears.</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!editor.hidden}
                onClick={() => update({ hidden: !editor.hidden })}
                data-testid="wizard-catalog-editor-visible-toggle"
                className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors ${editor.hidden ? "bg-slate-300" : "bg-[#1E5FEB]"}`}
              >
                <span className={`absolute h-5 w-5 rounded-full bg-white shadow transition-transform ${editor.hidden ? "translate-x-1" : "translate-x-6"}`} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-2" data-testid="wizard-catalog-editor-scope-mode">
              {scopeOptions.map((opt) => {
                const active = editor.scope_mode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => applyScopeMode(opt.id)}
                    data-testid={`wizard-catalog-scope-${opt.id}`}
                    className={`text-left rounded-xl border p-3 transition-all ${
                      active
                        ? "border-[#1E5FEB] bg-blue-50 ring-2 ring-blue-100"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-[#1E5FEB] bg-[#1E5FEB]" : "border-slate-300"}`}>
                        {active && <Check size={10} weight="bold" className="text-white" />}
                      </span>
                      <span className="text-sm font-bold text-slate-900">{opt.title}</span>
                    </div>
                    <div className="mt-1 pl-6 text-xs text-slate-500">{opt.sub}</div>
                  </button>
                );
              })}
            </div>

            {editor.scope_mode !== "all" && (
              <label className="block">
                <Label>Brand</Label>
                <select
                  value={brandId}
                  onChange={(e) => {
                    const nextBrand = e.target.value;
                    update({
                      parent_manufacturer: nextBrand,
                      parent_device_type: "",
                      parent_model: "",
                    });
                  }}
                  className={inputCls}
                  data-testid="wizard-catalog-editor-brand-select"
                >
                  <option value="">Choose brand</option>
                  {brandOptions.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </label>
            )}

            {editor.type !== "device_type" && editor.scope_mode !== "all" && brandId && (
              <label className="block">
                <Label>Device type</Label>
                <select
                  value={deviceId}
                  onChange={(e) => update({ parent_device_type: e.target.value, parent_model: "" })}
                  className={inputCls}
                  data-testid="wizard-catalog-editor-device-select"
                  disabled={!brandId}
                >
                  <option value="">Choose device type</option>
                  {deviceOptions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </label>
            )}

            {editor.type === "problem" && editor.scope_mode === "model" && (
              <label className="block">
                <Label>Model</Label>
                <select
                  value={editor.parent_model || ""}
                  onChange={(e) => update({ parent_model: e.target.value })}
                  className={inputCls}
                  data-testid="wizard-catalog-editor-model-select"
                  disabled={!brandId || !deviceId}
                >
                  <option value="">All models in this device type</option>
                  {modelOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>
            )}
          </div>

          {editor.confirmDelete && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="wizard-catalog-delete-confirmation">
              <div className="text-sm font-bold text-rose-900">Delete this item?</div>
              <div className="text-xs text-rose-700 mt-1">This removes it from the wizard catalog.</div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={saving}
                  data-testid="wizard-catalog-delete-confirm-button"
                  className="h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-bold"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => update({ confirmDelete: false })}
                  disabled={saving}
                  data-testid="wizard-catalog-delete-cancel-button"
                  className="h-9 px-3 rounded-lg border border-rose-200 text-rose-700 bg-white text-xs font-bold"
                >
                  Keep it
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between gap-3">
          {isAdmin ? (
            <button
              type="button"
              onClick={() => update({ confirmDelete: true })}
              disabled={saving}
              data-testid="wizard-catalog-editor-delete-button"
              className="h-10 px-3 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash size={13} weight="bold" /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="wizard-catalog-editor-cancel-button"
              className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 hover:border-slate-400 text-xs font-bold disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              data-testid="wizard-catalog-editor-save-button"
              className="h-10 px-5 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:opacity-50 text-white text-xs font-bold inline-flex items-center gap-1.5"
            >
              <Check size={13} weight="bold" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}


/**
 * Premium customer result card used inside the wizard picker. Shows all the
 * details staff needs at-a-glance (avatar, name, phone, email, address,
 * notes) plus a quick edit affordance and an out-of-wizard link into the
 * full customer profile (open in a new tab so the wizard stays put).
 */
function CustomerCard({ c, selected, onSelect, onEdit, openBookings = [] }) {
  const initials = (c.name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join("") || "?";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      data-testid={`wizard-cust-opt-${c.id}`}
      className={`group relative rounded-2xl border transition-[background-color,border-color,box-shadow,transform] cursor-pointer p-3.5 pr-4 overflow-hidden ${
        selected
          ? "border-[#1E5FEB] bg-blue-50/20 shadow-[0_2px_14px_rgba(15,23,42,0.08)]"
          : "border-slate-200 bg-white hover:border-[#1E5FEB]/40 hover:bg-blue-50/30 hover:shadow-[0_2px_10px_rgba(15,23,42,0.06)] hover:-translate-y-0.5"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-[13px] font-bold tracking-tight border ${selected ? "bg-white border-[#1E5FEB]/30 text-[#1E5FEB]" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`text-[14px] font-semibold truncate ${selected ? "text-[#1E5FEB]" : "text-slate-900"}`}>
              {c.name || "—"}
            </div>
            {selected && <CheckCircle size={16} weight="fill" className="text-[#1E5FEB] shrink-0" data-testid={`wizard-cust-selected-icon-${c.id}`} />}
            {openBookings.length > 0 && (
              <span
                className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold uppercase tracking-wider border border-[#FCD34D]"
                title={`${openBookings.length} unconverted booking${openBookings.length === 1 ? "" : "s"}`}
                data-testid={`wizard-cust-booking-flag-${c.id}`}
              >
                {openBookings.length} booking{openBookings.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
            {c.phone && (
              <div className="flex items-center gap-1.5 font-mono tabular-nums min-w-0">
                <Phone size={11} weight="bold" className="text-slate-400" />{c.phone}
              </div>
            )}
            {c.email && (
              <div className="flex items-center gap-1.5 truncate min-w-0 max-w-full">
                <EnvelopeSimple size={11} weight="bold" className="text-slate-400 shrink-0" />
                <span className="truncate">{c.email}</span>
              </div>
            )}
            {c.address && (
              <div className="basis-full text-[11px] text-slate-500 truncate pl-[0.875rem]">{c.address}</div>
            )}
            {c.notes && (
              <div className="basis-full text-[11px] text-slate-500 italic truncate pl-[0.875rem]">{c.notes}</div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onEdit?.(); } }}
            data-testid={`wizard-cust-edit-open-${c.id}`}
            title="Edit customer"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#1E5FEB] hover:bg-blue-50 transition-colors"
          >
            <PencilSimple size={12} weight="bold" />
          </span>
          <a
            href={`/customers/${c.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`wizard-cust-view-${c.id}`}
            title="View full profile"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#1E5FEB] hover:bg-blue-50 transition-colors"
          >
            <ArrowSquareOut size={12} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// WebsitePricesPanel — inline widget on the Issues step. Reads the
// synced website catalog and, if the currently-selected manufacturer +
// device type + model has a match, surfaces the live repair list with
// a part-option dropdown (OEM / Soft / Incell / etc). Selecting an
// option auto-adds it as a "custom problem" to the ticket wizard.
// ─────────────────────────────────────────────────────────────────────────
function WebsitePricesPanel({ websiteCatalog, manufacturer, deviceType, model, onAdd }) {
  const mfrs = websiteCatalog?.manufacturers || [];
  if (!mfrs.length) return null;

  const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");

  // Match manufacturer by label first, then id — website slugs are kebab-
  // case so we compare on normalized alnum for resilience.
  const selMfrKey = norm(manufacturer?.label || manufacturer?.id);
  const wMfr = mfrs.find((m) => norm(m.name) === selMfrKey || norm(m.id) === selMfrKey);
  if (!wMfr) return null;

  // The POS's device types are broad ("Phone", "Tablet") whereas the
  // website often uses specific ones ("iPhone", "iPad", "S Series"). So
  // instead of matching device_type strictly, we flatten every model
  // under this manufacturer and match by model name only. That gives us
  // the maximum recall and matches how staff mentally look things up.
  const allWebsiteModels = (wMfr.device_types || []).flatMap((dt) =>
    (dt.models || []).map((m) => ({ ...m, _deviceTypeName: dt.name }))
  );
  const selModelKey = norm(model);
  if (!selModelKey) return null;
  const wModel = allWebsiteModels.find((m) => {
    const k = norm(m.name);
    return k === selModelKey || k.includes(selModelKey) || selModelKey.includes(k);
  });
  if (!wModel) return null;

  const repairs = wModel.repairs || [];
  if (!repairs.length) return null;

  return (
    <div className="mb-4 rounded-2xl border border-blue-200/70 bg-gradient-to-br from-blue-50/70 to-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-6 h-6 rounded-lg bg-[#1E5FEB]/10 flex items-center justify-center shrink-0">
            <Globe size={13} weight="bold" className="text-[#1E5FEB]" />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#1E5FEB]">
              Website prices
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {wMfr.name} · {wModel.name}{wModel.year ? ` (${wModel.year})` : ""}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-slate-400 font-medium shrink-0">
          {repairs.length} repair{repairs.length === 1 ? "" : "s"} live
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="website-repairs-grid">
        {repairs.map((r) => (
          <WebsiteRepairPill key={r.id} repair={r} onAdd={onAdd} />
        ))}
      </div>
    </div>
  );
}

function WebsiteRepairPill({ repair, onAdd }) {
  const options = repair.part_options || [];
  const single = options.length === 1;
  const [selectedId, setSelectedId] = useState(options[0]?.id || "");
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === selectedId) || options[0];

  const handleAdd = () => {
    if (!selected) return;
    // When there's only one option (e.g. "Standard"), we skip the option
    // label in the ticket line — the repair name alone is clearer.
    const label = single ? repair.name : `${repair.name} — ${selected.label}`;
    onAdd(label, selected.price);
  };

  return (
    <div className="rounded-xl border border-blue-200/70 bg-white p-3 flex items-center gap-3 hover:border-[#1E5FEB]/60 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate" title={repair.name}>{repair.name}</div>
        {!single ? (
          <div className="relative mt-1.5" data-testid={`website-repair-${repair.id}`}>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="w-full h-8 pl-3 pr-8 rounded-lg border border-slate-200 hover:border-slate-400 bg-white text-left text-xs font-medium text-slate-700 flex items-center justify-between transition-colors"
              data-testid={`website-repair-${repair.id}-dropdown`}
            >
              <span className="truncate">{selected.label} · ${Number(selected.price || 0).toFixed(2)}</span>
              <CaretDown size={11} weight="bold" className="text-slate-400 shrink-0" />
            </button>
            {open && (
              <div className="absolute left-0 right-0 mt-1 rounded-xl border border-slate-200 bg-white shadow-lg z-10 overflow-hidden">
                {options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => { setSelectedId(o.id); setOpen(false); }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between gap-3 transition-colors ${o.id === selectedId ? "bg-blue-50 text-[#1E5FEB]" : "hover:bg-slate-50 text-slate-700"}`}
                    data-testid={`website-part-${repair.id}-${o.id}`}
                  >
                    <span className="font-medium truncate">{o.label}</span>
                    <span className="font-mono text-slate-500 shrink-0">${Number(o.price || 0).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-0.5 text-[11px] font-mono text-slate-500">${Number(selected.price || 0).toFixed(2)}</div>
        )}
      </div>
      <button
        type="button"
        onClick={handleAdd}
        data-testid={`website-repair-${repair.id}-add`}
        className="h-8 px-3 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[11px] font-semibold flex items-center gap-1 shrink-0"
      >
        <Plus size={11} weight="bold" /> Add
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BrandAvatar — tries to render the manufacturer's real SVG logo from
// simpleicons.org CDN. Falls back to a coloured monogram if the brand
// isn't on the CDN (or the fetch fails offline). Every brand ends up
// with SOMETHING recognisable — no broken image icons ever.
// ─────────────────────────────────────────────────────────────────────────
function BrandAvatar({ id, label }) {
  const [failed, setFailed] = useState(false);
  const url = brandLogoUrl(id);
  if (failed || !url) {
    const c = brandColour(id);
    return (
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-[13px] tracking-tight ${c.bg} ${c.text}`}>
        {brandMonogram(label)}
      </div>
    );
  }
  return (
    <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center p-1.5 shadow-sm">
      <img
        src={url}
        alt={label}
        onError={() => setFailed(true)}
        className="max-w-full max-h-full object-contain"
        loading="lazy"
      />
    </div>
  );
}

